<template>
  <div>
    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.leave_area }}
        <small class='calculate-price__pts-hint' v-if='s.surface_points_per_m2'>{{ formatPts(s.surface_points_per_m2) }} pt/m²</small>
      </h2>
      <div class='mb-10' v-for='(room, index) in localRooms' :key='index'>
        <h3 class='calculate-price__form-subtitle calculate-price__room-name'>
          <template v-if='propertyFacilityMap && propertyFacilityMap[room]'>
            {{ localizedName(propertyFacilityMap[room]) }}
          </template>
          <template v-else-if="room.indexOf('extra_') === 0">
            <select
              v-model='leaveArea[room].facility_metadata_id'
              @change="onExtraRoomSelect(room, 'room')"
              :name='`leaveArea[` + room + `][facility_metadata_id]`'
              class='calculate-price__room-type-select'>
              <option value=''>-- {{ locales && locales.properties && locales.properties.select_room_type  }} --</option>
              <option v-for='f in (facilityOptionsByCategory.room || [])' :key='f.id' :value='f.id'>{{ localizedName(f) }}</option>
            </select>
            <span v-if='!leaveArea[room].facility_metadata_id && (leaveArea[room].name_nl || leaveArea[room].name_en)' class='calculate-price__room-legacy-label'>
              ({{ localizedName(leaveArea[room]) }})
            </span>
          </template>
          <template v-else>
            {{ localizedName(facilityTypes && facilityTypes[room]) }}
            <span v-if="room == 'other'">{{ locales && locales.properties && locales.properties.rooms }}</span>
          </template>
        </h3>
        <template v-if="room.indexOf('extra_') === 0">
          <input type='hidden' :name='`leaveArea[` + room + `][name_nl]`' :value='leaveArea[room].name_nl'/>
          <input type='hidden' :name='`leaveArea[` + room + `][name_en]`' :value='leaveArea[room].name_en'/>
        </template>
        <template v-else>
          <input
            type='hidden'
            :name='`leaveArea[` + room + `][name_nl]`'
            :value="propertyFacilityMap && propertyFacilityMap[room]
              ? propertyFacilityMap[room].name_nl
              : ((facilityTypes[room] && facilityTypes[room]['value_lang']['nl']) + (room == 'other' ? locales && locales.properties && locales.properties.rooms : ''))"
          />
          <input
            type='hidden'
            :name='`leaveArea[` + room + `][name_en]`'
            :value="propertyFacilityMap && propertyFacilityMap[room]
              ? propertyFacilityMap[room].name_en
              : ((facilityTypes[room] && facilityTypes[room]['value_lang']['en']) + (room == 'other' ? locales && locales.properties && locales.properties.rooms : ''))"
          />
        </template>
        <div class='grid grid-cols-3 gap-2 sm:gap-5 mb-3'>
          <div>
            <theme-numeric
              v-model='leaveArea[room].length'
              valid-scope='modal-form'
              :name='`leaveArea[` + room + `][length]`'
              :label="locales && locales.properties && locales.properties.room_length || 'Lengte (m)'"
              @input="calculateSurface(leaveArea, room, 'leaveArea')">
            </theme-numeric>
          </div>
          <div>
            <theme-numeric
              v-model='leaveArea[room].width'
              valid-scope='modal-form'
              :name='`leaveArea[` + room + `][width]`'
              :label="locales && locales.properties && locales.properties.room_width || 'Breedte (m)'"
              @input="calculateSurface(leaveArea, room, 'leaveArea')">
            </theme-numeric>
          </div>
          <div>
            <theme-numeric
              v-model='leaveArea[room].surface'
              valid-scope='modal-form'
              :name='`leaveArea[` + room + `][surface]`'
              :label="locales && locales.properties && locales.properties.room_surface || 'Oppervlak'"
              @input='countLeaveAreaPoints(leaveArea)'
              :IsExtraInfo='true'
              :extra-info-suffix='true'
            >
              <template slot='extra-info'> m<sup>2</sup></template>
            </theme-numeric>
          </div>
        </div>
        <div
          v-for='(dim, dIdx) in (leaveArea[room].extra_dimensions || [])'
          :key='"leave_extra_dim_" + index + "_" + dIdx'
          class='grid grid-cols-3 gap-2 sm:gap-5 mb-3 items-end'>
          <div>
            <theme-numeric
              v-model='dim.length'
              valid-scope='modal-form'
              :name='`leaveArea[` + room + `][extra_dimensions][` + dIdx + `][length]`'
              :label="locales && locales.properties && locales.properties.room_length || 'Lengte (m)'"
              @input="calculateSurface(leaveArea, room, 'leaveArea')">
            </theme-numeric>
          </div>
          <div>
            <theme-numeric
              v-model='dim.width'
              valid-scope='modal-form'
              :name='`leaveArea[` + room + `][extra_dimensions][` + dIdx + `][width]`'
              :label="locales && locales.properties && locales.properties.room_width || 'Breedte (m)'"
              @input="calculateSurface(leaveArea, room, 'leaveArea')">
            </theme-numeric>
          </div>
          <div>
            <button type='button' class='calculate-price__remove-dim-btn' @click='removeExtraDimension(leaveArea, room, dIdx, "leaveArea")'>
              {{ locales && locales.properties && locales.properties.remove_dimension_row || 'Verwijderen' }}
            </button>
          </div>
        </div>
        <div class='mb-5'>
          <button type='button' class='calculate-price__add-room-btn' @click='addExtraDimension(leaveArea, room)'>
            + {{ locales && locales.properties && locales.properties.add_dimension_row || 'Lengte en breedte veld toevoegen' }}
          </button>
        </div>
        <div class='flex flex-wrap items-center gap-6 mb-5'>
          <div>
            <input type='hidden' :name='`leaveArea[` + room + `][heated]`' value='0'/>
            <theme-checkboxes
              :id='`leaveArea_heated_` + room'
              @input='countLeaveAreaHeatedCooled'
              label=''
              :is-single-checkbox='true'
              :name='`leaveArea[` + room + `][heated]`'
              v-model='leaveArea[room].heated'
              :options="{ '1': `${locales && locales.properties && locales.properties.heated || 'Verwarmd'} ${pointsLabel(s.heating && s.heating.points_per_room)}` }">
            </theme-checkboxes>
            <template v-if='leaveArea[room] && leaveArea[room].heated == 1'>
              <input type='hidden' :name='`leaveArea[` + room + `][heated]`' value='1'/>
            </template>
          </div>
          <div>
            <input type='hidden' :name='`leaveArea[` + room + `][cooled]`' value='0'/>
            <theme-checkboxes
              :id='`leaveArea_cooled_` + room'
              @input='countLeaveAreaHeatedCooled'
              label=''
              :is-single-checkbox='true'
              :name='`leaveArea[` + room + `][cooled]`'
              v-model='leaveArea[room].cooled'
              :options="{ '1': `${locales && locales.properties && locales.properties.cooled} ${pointsLabel(s.heating && s.heating.points_per_cooled_room, {suffix: locales && locales.properties && locales.properties.per_cooled_room})}` }">
            </theme-checkboxes>
            <template v-if='leaveArea[room] && leaveArea[room].cooled == 1'>
              <input type='hidden' :name='`leaveArea[` + room + `][cooled]`' value='1'/>
            </template>
          </div>
        </div>
      </div>
      <div class='mb-5'>
        <button type='button' class='calculate-price__add-room-btn' @click='addRoom'>
          + {{ locales && locales.properties && locales.properties.add_room  }}
        </button>
      </div>
      <div class='mb-10'>
        <input type='hidden' name='leaveArea[include_shower]' value='0'/>
        <theme-checkboxes
          id='includeShower'
          @input='countLeaveAreaPoints(leaveArea)'
          label=''
          :is-single-checkbox='true'
          name='leaveArea[include_shower]'
          v-model='leaveArea.include_shower'
          :options="{ '1': `${locales && locales.properties && locales.properties.toilet_in_shower_or_bathroom} ${pointsLabel(-Math.abs(parseFloat((((s.sanitary && s.sanitary.negative_toilet_in_bathroom_points) || 1)+'').replace(',', '.'))))}` }">
        </theme-checkboxes>
        <template v-if='leaveArea.include_shower == 1'>
          <input type='hidden' name='leaveArea[include_shower]' value='1'/>
        </template>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='leaveArea.total'
            valid-scope='modal-form'
            name='leaveArea[total]'
            :label='locales && locales.properties && locales.properties.leave_area_points_total'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.other_spaces }}
        <small class='calculate-price__pts-hint' v-if='s.other_spaces_points_per_m2'>{{ formatPts(s.other_spaces_points_per_m2) }} pt/m²</small>
      </h2>
      <div class='mb-10' v-for='(room, index) in localOtherRooms' :key='index'>
        <h3 class='calculate-price__form-subtitle calculate-price__room-name'>
          <template v-if='propertyFacilityMap && propertyFacilityMap[room]'>
            {{ localizedName(propertyFacilityMap[room]) }}
          </template>
          <template v-else-if="room.indexOf('extra_other_') === 0">
            <select
              v-model='otherSpaces[room].facility_metadata_id'
              @change="onExtraRoomSelect(room, 'other_room')"
              :name='`otherSpaces[` + room + `][facility_metadata_id]`'
              class='calculate-price__room-type-select'>
              <option value=''>-- {{ locales && locales.properties && locales.properties.select_room_type  }} --</option>
              <option v-for='f in (facilityOptionsByCategory.other_room || [])' :key='f.id' :value='f.id'>{{ localizedName(f) }}</option>
            </select>
            <span v-if='!otherSpaces[room].facility_metadata_id && (otherSpaces[room].name_nl || otherSpaces[room].name_en)' class='calculate-price__room-legacy-label'>
              ({{ localizedName(otherSpaces[room]) }})
            </span>
          </template>
          <template v-else>
            <span v-if="room == 'other'">{{ locales && locales.properties && locales.properties.other_spaces }}</span>
            <span v-else>{{ localizedName(facilityTypes && facilityTypes[room]) }}</span>
          </template>
        </h3>
        <template v-if="room.indexOf('extra_other_') === 0">
          <input type='hidden' :name='`otherSpaces[` + room + `][name_nl]`' :value='otherSpaces[room].name_nl'/>
          <input type='hidden' :name='`otherSpaces[` + room + `][name_en]`' :value='otherSpaces[room].name_en'/>
        </template>
        <template v-else>
          <input
            type='hidden'
            :name='`otherSpaces[` + room + `][name_nl]`'
            :value="propertyFacilityMap && propertyFacilityMap[room]
              ? propertyFacilityMap[room].name_nl
              : ((facilityTypes[room] && facilityTypes[room]['value_lang']['nl']) + (room == 'other' ? locales && locales.properties && locales.properties.rooms : ''))"
          />
          <input
            type='hidden'
            :name='`otherSpaces[` + room + `][name_en]`'
            :value="propertyFacilityMap && propertyFacilityMap[room]
              ? propertyFacilityMap[room].name_en
              : ((facilityTypes[room] && facilityTypes[room]['value_lang']['en']) + (room == 'other' ? locales && locales.properties && locales.properties.rooms : ''))"
          />
        </template>
        <div class='grid grid-cols-3 gap-2 sm:gap-5 mb-3'>
          <div>
            <theme-numeric
              v-model='otherSpaces[room].length'
              valid-scope='modal-form'
              :name='`otherSpaces[` + room + `][length]`'
              :label="locales && locales.properties && locales.properties.room_length || 'Lengte (m)'"
              @input="calculateSurface(otherSpaces, room, 'otherSpaces')">
            </theme-numeric>
          </div>
          <div>
            <theme-numeric
              v-model='otherSpaces[room].width'
              valid-scope='modal-form'
              :name='`otherSpaces[` + room + `][width]`'
              :label="locales && locales.properties && locales.properties.room_width || 'Breedte (m)'"
              @input="calculateSurface(otherSpaces, room, 'otherSpaces')">
            </theme-numeric>
          </div>
          <div>
            <theme-numeric
              v-model='otherSpaces[room].surface'
              valid-scope='modal-form'
              :name='`otherSpaces[` + room + `][surface]`'
              :label="locales && locales.properties && locales.properties.room_surface || 'Oppervlak'"
              @input='countOtherSpacesPoints(otherSpaces)'
              :IsExtraInfo='true'
              :extra-info-suffix='true'
            >
              <template slot='extra-info'> m<sup>2</sup></template>
            </theme-numeric>
          </div>
        </div>
        <div
          v-for='(dim, dIdx) in (otherSpaces[room].extra_dimensions || [])'
          :key='"other_extra_dim_" + index + "_" + dIdx'
          class='grid grid-cols-3 gap-2 sm:gap-5 mb-3 items-end'>
          <div>
            <theme-numeric
              v-model='dim.length'
              valid-scope='modal-form'
              :name='`otherSpaces[` + room + `][extra_dimensions][` + dIdx + `][length]`'
              :label="locales && locales.properties && locales.properties.room_length || 'Lengte (m)'"
              @input="calculateSurface(otherSpaces, room, 'otherSpaces')">
            </theme-numeric>
          </div>
          <div>
            <theme-numeric
              v-model='dim.width'
              valid-scope='modal-form'
              :name='`otherSpaces[` + room + `][extra_dimensions][` + dIdx + `][width]`'
              :label="locales && locales.properties && locales.properties.room_width || 'Breedte (m)'"
              @input="calculateSurface(otherSpaces, room, 'otherSpaces')">
            </theme-numeric>
          </div>
          <div>
            <button type='button' class='calculate-price__remove-dim-btn' @click='removeExtraDimension(otherSpaces, room, dIdx, "otherSpaces")'>
              {{ locales && locales.properties && locales.properties.remove_dimension_row || 'Verwijderen' }}
            </button>
          </div>
        </div>
        <div class='mb-5'>
          <button type='button' class='calculate-price__add-room-btn' @click='addExtraDimension(otherSpaces, room)'>
            + {{ locales && locales.properties && locales.properties.add_dimension_row || 'Lengte en breedte veld toevoegen' }}
          </button>
        </div>
        <div class='flex flex-wrap items-center gap-6 mb-5'>
          <div>
            <input type='hidden' :name='`otherSpaces[` + room + `][heated]`' value='0'/>
            <theme-checkboxes
              :id='`otherSpaces_heated_` + room'
              @input='countLeaveAreaHeatedCooled'
              label=''
              :is-single-checkbox='true'
              :name='`otherSpaces[` + room + `][heated]`'
              v-model='otherSpaces[room].heated'
              :options="{ '1': `${locales && locales.properties && locales.properties.heated || 'Verwarmd'} ${pointsLabel(s.heating && s.heating.points_per_other_room_with_heating)}` }">
            </theme-checkboxes>
            <template v-if='otherSpaces[room] && otherSpaces[room].heated == 1'>
              <input type='hidden' :name='`otherSpaces[` + room + `][heated]`' value='1'/>
            </template>
          </div>
          <div>
            <input type='hidden' :name='`otherSpaces[` + room + `][cooled]`' value='0'/>
            <theme-checkboxes
              :id='`otherSpaces_cooled_` + room'
              @input='countLeaveAreaHeatedCooled'
              label=''
              :is-single-checkbox='true'
              :name='`otherSpaces[` + room + `][cooled]`'
              v-model='otherSpaces[room].cooled'
              :options="{ '1': `${locales && locales.properties && locales.properties.cooled} ${pointsLabel(s.heating && s.heating.points_per_other_room_with_cooling)}` }">
            </theme-checkboxes>
            <template v-if='otherSpaces[room] && otherSpaces[room].cooled == 1'>
              <input type='hidden' :name='`otherSpaces[` + room + `][cooled]`' value='1'/>
            </template>
          </div>
        </div>
      </div>
      <div class='mb-5'>
        <button type='button' class='calculate-price__add-room-btn' @click='addOtherRoom'>
          + {{ locales && locales.properties && locales.properties.add_room  }}
        </button>
      </div>
      <div class='mb-10'>
        <input
          type='hidden'
          name='otherSpaces[attic_no_staircase]'
          value='0'
        />
        <theme-checkboxes
          id='attic_no_staircase'
          @input='countOtherSpacesPoints(otherSpaces)'
          label=''
          :is-single-checkbox='true'
          name='otherSpaces[attic_no_staircase]'
          v-model='otherSpaces.attic_no_staircase'
          :options="{ '1': `${locales && locales.properties && locales.properties.attic_no_staircase} ${pointsLabel(-Math.abs(parseFloat((((s.attic_no_staircase_points_reduction) || 5)+'').replace(',', '.'))))}` }">
        </theme-checkboxes>
        <template v-if='otherSpaces.attic_no_staircase == 1'>
          <input type='hidden' name='otherSpaces[attic_no_staircase]' value='1'/>
        </template>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='otherSpaces.total'
            valid-scope='modal-form'
            name='otherSpaces[total]'
            :label='locales && locales.properties && locales.properties.other_spaces_points_total'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.heating }}
        <small class='calculate-price__pts-hint' v-if='s.heating && s.heating.points_per_room'>{{ formatPts(s.heating.points_per_room) }} pt/{{ locales && locales.properties && locales.properties.heated || 'vertrek' }}</small>
      </h2>
      <input type='hidden' name='heating[heated_rooms]' :value='heating.heated_rooms'/>
      <input type='hidden' name='heating[cooled_rooms]' :value='heating.cooled_rooms'/>
      <input type='hidden' name='heating[other_rooms_with_heating]' :value='heating.other_rooms_with_heating'/>
      <input type='hidden' name='heating[other_rooms_with_cooling]' :value='heating.other_rooms_with_cooling'/>
      <div class='grid grid-cols-1 lg:grid-cols-4 gap-5 mb-10 items-end'>
        <div>
          <theme-numeric
            :label="`${locales && locales.properties && locales.properties.number_of_heated_rooms}`"
            v-model='heating.heated_rooms'
            :isReadonly='hasFacilityRooms'>
          </theme-numeric>
        </div>
        <div>
          <theme-numeric
            :label="`${locales && locales.properties && locales.properties.number_of_cooled_rooms}`"
            v-model='heating.cooled_rooms'
            :isReadonly='hasFacilityRooms'>
          </theme-numeric>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-4 gap-5 mb-10 items-end'>
        <div>
          <theme-numeric
            :label="`${locales && locales.properties && locales.properties.number_of_other_rooms_with_heating}${s.heating && s.heating.max_other_heating_points ? ' (max ' + formatPts(s.heating.max_other_heating_points) + ' pt)' : ''}`"
            v-model='heating.other_rooms_with_heating'
            :isReadonly='hasFacilityRooms'>
          </theme-numeric>
        </div>
        <div>
          <theme-numeric
            :label="`${locales && locales.properties && locales.properties.number_of_other_cooled_rooms}`"
            v-model='heating.other_rooms_with_cooling'
            :isReadonly='hasFacilityRooms'>
          </theme-numeric>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='heating.total'
            valid-scope='modal-form'
            name='heating[total]'
            :label='locales && locales.properties && locales.properties.total_heating'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.energy_performance }}
        <small class='calculate-price__pts-hint' v-if='maxEnergyPts !== null'>tot {{ formatPts(maxEnergyPts) }} pt</small>
      </h2>
      <div class='mb-10'>
        <h3 class='calculate-price__form-subtitle'>
          {{ locales && locales.properties && locales.properties.property_type }}
        </h3>
        <theme-radios
          :columnDirection='true'
          v-model='energy.property_type'
          name='energy[property_type]'
          :options="{
              '1':
                locales &&
                locales.properties &&
                locales.properties.single_family_home,
              '2':
                locales &&
                locales.properties &&
                locales.properties.multi_family_home,
            }"
          @input='countEnergyPoints'>
        </theme-radios>
      </div>
      <div v-if='energy.energy_performance_fee != 1' class='mb-10'>
        <h3 class='calculate-price__form-subtitle'>
          {{ locales && locales.properties && locales.properties.energy_label }}
          <strong v-if="energy.energy_label && energyLabels[energy.energy_label] && energyLabels[energy.energy_label]['slug'] != 'no_label_or_index'">
            {{ energy.energy_points || '' }}
          </strong>
        </h3>
        <div class='mb-10'>
          <theme-radios
            v-model='energy.energy_label'
            name='energy[energy_label]'
            :options='energyLabelsOptions'
            :sort='true'
            sort-by='custom'
            :custom-order='energyLabelsCustomOrderWithPoints'
            @input='countEnergyPoints'>
          </theme-radios>
        </div>
        <div v-if="energy.energy_label && energyLabels[energy.energy_label] && energyLabels[energy.energy_label]['slug'] == 'no_label_or_index'" class='grid grid-cols-1 lg:grid-cols-12'>
          <div class='col-span-4'>
            <theme-input
              @input='countEnergyPoints'
              :label="`${locales &&locales.properties && locales.properties.construction_year} ${energy.building_construction_year && energy.energy_points || ''}`"
              v-model='energy.building_construction_year'
              name='energy[building_construction_year]'>
            </theme-input>
          </div>
        </div>
      </div>
      <div class='mb-8'>
        <input type='hidden' name='energy[energy_performance_fee]' value='0'/>
        <theme-checkboxes
          id='energy_performance_fee'
          @input='countEnergyPoints'
          label=''
          :is-single-checkbox='true'
          :big-label='true'
          name='energy[energy_performance_fee]'
          v-model='energy.energy_performance_fee'
          :options="{ '1': `${locales && locales.properties && locales.properties.energy_performance_fee} ${energy.energy_performance_fee == 1 ? energy.energy_points : ''}` }">
        </theme-checkboxes>
        <template v-if='energy.energy_performance_fee == 1'>
          <input type='hidden' name='energy[energy_performance_fee]' value='1'/>
        </template>
      </div>
      <!-- 2026+: monument type selector (affects energy floor and rent surcharge) -->
      <template v-if='new Date(independantSettings.up_to_date) >= newVersion'>
        <div class='mb-5 calculate-price__form-divider'>
          <h3 class='calculate-price__form-subtitle calculate-price__room-name'>
            {{ locales && locales.properties && locales.properties.monument_type || 'Monument / protected view status' }}
          </h3>
          <input type='hidden' name='special_features[monument_type]' value='1'/>
          <theme-radios
            :columnDirection='true'
            v-model='specialFeatures.monument_type.value'
            name='special_features[monument_type]'
            @input='countSpecialFeaturesPoints'
            :options='monumentTypeOptions'>
          </theme-radios>
        </div>
      </template>
      <!-- Pre-2026: legacy monument_allowance checkbox -->
      <template v-else>
        <div class='mb-5'>
          <input type='hidden' name='monument_allowance[value]' value='0'/>
          <theme-checkboxes
            id='monumentAllowance'
            @input='countEnergyPoints'
            label=''
            :is-single-checkbox='true'
            name='monument_allowance[value]'
            v-model='monumentAllowance.value'
            :options="{ '1': `${locales && locales.properties && locales.properties.monument_allowance_energy} ${pointsLabel(50)}` }">
          </theme-checkboxes>
          <template v-if='monumentAllowance.value == 1'>
            <input type='hidden' name='monument_allowance[value]' value='1'/>
          </template>
        </div>
      </template>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='energy.total'
            valid-scope='modal-form'
            name='energy[total]'
            :label='locales && locales.properties && locales.properties.total_energy'
            :precision='0'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.kitchen }}
        <small class='calculate-price__pts-hint' v-if='s.kitchen && s.kitchen.worktop_2m_max_points'>aanrecht tot {{ formatPts(s.kitchen.worktop_2m_max_points) }} pt</small>
      </h2>
      <div class='grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10'>
        <div>
          <h3 class='calculate-price__form-subtitle'>
            {{ locales && locales.properties && locales.properties.worktop_length }}
            <strong v-if='kitchen.worktop_length.value'>{{ kitchen.worktop_length.total || '' }}</strong>
          </h3>
          <theme-radios
            :columnDirection='true'
            v-model='kitchen.worktop_length.value'
            name='kitchen[worktop_length]'
            :options="{
              '1': `${locales && locales.properties && locales.properties.less_than_1_meter} ${pointsLabel(0)}`,
              '2': `${locales && locales.properties && locales.properties['1_2_meters']} ${pointsLabel(s.kitchen && s.kitchen.worktop_1_2m_points)}`,
              '3': `${locales && locales.properties && locales.properties.more_than_2_meters} ${pointsLabel(s.kitchen && s.kitchen.worktop_2m_points)}`,
            }"
            @input='countKitchenPoints'>
          </theme-radios>
        </div>
        <div>
          <h3 class='calculate-price__form-subtitle'>
            {{ locales && locales.properties && locales.properties.cooker }}
            <strong v-if='kitchen.cooker.value'>{{ kitchen.cooker.total || '' }}</strong>
          </h3>
          <theme-radios
            :columnDirection='true'
            v-model='kitchen.cooker.value'
            name='kitchen[cooker]'
            :options="{
              '1': `${locales && locales.properties && locales.properties.built_in_gas_hob} ${pointsLabel(s.kitchen && s.kitchen.gas_cooker_points)}`,
              '2': `${locales && locales.properties && locales.properties.built_in_ceramic_hob} ${pointsLabel(s.kitchen && s.kitchen.ceramic_cooker_points)}`,
              '3': `${locales && locales.properties && locales.properties.built_in_induction_hob} ${pointsLabel(s.kitchen && s.kitchen.induction_cooker_points)}`,
            }"
            @input='countKitchenPoints'>
          </theme-radios>
        </div>
        <div>
          <h3 class='calculate-price__form-subtitle'>
            {{ locales && locales.properties && locales.properties.oven }}
            <strong v-if='kitchen.oven.value'>{{ kitchen.oven.total || '' }}</strong>
          </h3>
          <theme-radios
            :columnDirection='true'
            v-model='kitchen.oven.value'
            name='kitchen[oven]'
            :options="{
              '1': `${locales && locales.properties && locales.properties.built_in_gas_oven} ${pointsLabel(s.kitchen && s.kitchen.gas_oven_points)}`,
              '2': `${locales && locales.properties && locales.properties.built_in_electric_oven} ${pointsLabel(s.kitchen && s.kitchen.electric_oven_points)}`,
            }"
            @input='countKitchenPoints'>
          </theme-radios>
        </div>
      </div>
      <div class='mb-10'>
        <div class='grid grid-cols-1 lg:grid-cols-5 gap-5'>
          <div>
            <input type='hidden' name='kitchen[built_in_microwave]' value='0'/>
            <theme-checkboxes
              id='built_in_microwave'
              @input='countKitchenPoints'
              label=''
              :is-single-checkbox='true'
              name='kitchen[built_in_microwave]'
              v-model='kitchen.built_in_microwave.value'
              :options="{ '1': `${locales && locales.properties && locales.properties.built_in_microwave} ${pointsLabel(s.kitchen && s.kitchen.built_in_microwave_points)}` }">
            </theme-checkboxes>
          </div>
          <div>
            <input type='hidden' name='kitchen[built_in_extractor_hood]' value='0'/>
            <theme-checkboxes
              id='built_in_extractor_hood'
              @input='countKitchenPoints'
              label=''
              :is-single-checkbox='true'
              name='kitchen[built_in_extractor_hood]'
              v-model='kitchen.built_in_extractor_hood.value'
              :options="{ '1': `${locales && locales.properties && locales.properties.built_in_extractor_hood} ${pointsLabel(s.kitchen && s.kitchen.built_in_extractor_hood_points)}` }">
            </theme-checkboxes>
          </div>
          <div>
            <input type='hidden' name='kitchen[built_in_fridge]' value='0'/>
            <theme-checkboxes
              id='built_in_fridge'
              @input='countKitchenPoints'
              label=''
              :is-single-checkbox='true'
              name='kitchen[built_in_fridge]'
              v-model='kitchen.built_in_fridge.value'
              :options="{ '1': `${locales && locales.properties && locales.properties.built_in_fridge} ${pointsLabel(s.kitchen && s.kitchen.built_in_fridge_points)}` }">
            </theme-checkboxes>
          </div>
          <div>
            <input type='hidden' name='kitchen[built_in_freezer]' value='0'/>
            <theme-checkboxes
              id='built_in_freezer'
              @input='countKitchenPoints'
              label=''
              :is-single-checkbox='true'
              name='kitchen[built_in_freezer]'
              v-model='kitchen.built_in_freezer.value'
              :options="{ '1': `${locales && locales.properties && locales.properties.built_in_freezer} ${pointsLabel(s.kitchen && s.kitchen.built_in_freezer_points)}` }">
            </theme-checkboxes>
          </div>
          <div>
            <input type='hidden' name='kitchen[built_in_dishwasher]' value='0'/>
            <theme-checkboxes
              id='built_in_dishwasher'
              @input='countKitchenPoints'
              label=''
              :is-single-checkbox='true'
              name='kitchen[built_in_dishwasher]'
              v-model='kitchen.built_in_dishwasher.value'
              :options="{ '1': `${locales && locales.properties && locales.properties.built_in_dishwasher} ${pointsLabel(s.kitchen && s.kitchen.built_in_dishwasher_points)}` }">
            </theme-checkboxes>
          </div>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-4 gap-5 mb-10 items-end'>
        <div>
          <theme-numeric
            v-model='kitchen.luxury_mixer_tap.value'
            valid-scope='modal-form'
            name='kitchen[luxury_mixer_tap]'
            :label="`${locales && locales.properties && locales.properties.luxury_mixer_tap} ${pointsLabel(s.kitchen && s.kitchen.luxury_mixer_tap_points)} ${kitchen.luxury_mixer_tap.value ? kitchen.luxury_mixer_tap.total : ''}`"
            :precision='0'
            @input='countKitchenPoints'>
          </theme-numeric>
        </div>
        <div>
          <theme-numeric
            v-model='kitchen.thermostatic_water_mixer.value'
            valid-scope='modal-form'
            name='kitchen[thermostatic_water_mixer]'
            :label="`${locales && locales.properties && locales.properties.thermostatic_water_mixer} ${pointsLabel(s.kitchen && s.kitchen.thermostatic_water_mixer_points)} ${kitchen.thermostatic_water_mixer.value ? kitchen.thermostatic_water_mixer.total : ''}`"
            :precision='0'
            @input='countKitchenPoints'>
          </theme-numeric>
        </div>
        <div>
          <theme-numeric
            v-model='kitchen.extra_single_cupboard.value'
            valid-scope='modal-form'
            name='kitchen[extra_single_cupboard]'
            :label="`${locales && locales.properties && locales.properties.extra_single_cupboard} ${pointsLabel(s.kitchen && s.kitchen.extra_single_cupboard_points)} ${kitchen.extra_single_cupboard.value ? kitchen.extra_single_cupboard.total : ''}`"
            :precision='0'
            @input='countKitchenPoints'>
          </theme-numeric>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='kitchen.total'
            valid-scope='modal-form'
            name='kitchen[total]'
            :label='locales && locales.properties && locales.properties.total_kitchen'
            :precision='2'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.sanitary }}
      </h2>
      <div class='grid grid-cols-1 lg:grid-cols-4 gap-5 mb-10 items-end'>
        <div>
          <theme-input
            @input='countSanitaryPoints'
            :label="`${locales && locales.properties && locales.properties.number_of_toilets} ${sanitary.number_of_toilets.value && sanitary.number_of_toilets.total || ''}`"
            v-model='sanitary.number_of_toilets.value'
            name='sanitary[number_of_toilets]'>
          </theme-input>
        </div>
        <div v-if="sanitary.number_of_toilets.value > 0">
          <theme-input
            @input='countSanitaryPoints'
            :label="`${locales && locales.properties && locales.properties.number_of_hunged_toilets} ${sanitary.number_of_hunged_toilets.value && sanitary.number_of_hunged_toilets.total || ''}`"
            v-model='sanitary.number_of_hunged_toilets.value'
            name='sanitary[number_of_hunged_toilets]'>
          </theme-input>
        </div>
        <div v-if="sanitary.number_of_toilets.value > 0">
          <theme-input
            @input='countSanitaryPoints'
            :label="`${locales && locales.properties && locales.properties.number_of_separate_toilets} ${sanitary.number_of_separate_toilets.value && sanitary.number_of_separate_toilets.total || ''}`"
            v-model='sanitary.number_of_separate_toilets.value'
            name='sanitary[number_of_separate_toilets]'>
          </theme-input>
        </div>
        <div>
          <theme-input
            @input='countSanitaryPoints'
            :label="`${locales && locales.properties && locales.properties.number_of_washbasins} ${sanitary.number_of_washbasins.value && sanitary.number_of_washbasins.total || ''}`"
            v-model='sanitary.number_of_washbasins.value'
            name='sanitary[number_of_washbasins]'>
          </theme-input>
        </div>
      </div>
      <div class='mb-10'>
        <h3 class='calculate-price__form-subtitle'>
          {{ locales && locales.properties && locales.properties.sanitary }}
          <strong v-if='sanitary.shower_bath.value'>{{ sanitary.shower_bath.total || '' }}</strong>
        </h3>
        <theme-radios
          @input='countSanitaryPoints'
          label=''
          name='sanitary[shower_bath]'
          v-model='sanitary.shower_bath.value'
          :options="{
              '0': `${locales && locales.properties && locales.properties.no_shower_or_bath} ${pointsLabel(0)}`,
              '1': `${locales && locales.properties && locales.properties.shower_available} ${pointsLabel(s.sanitary && s.sanitary.shower_points)}`,
              '2': `${locales && locales.properties && locales.properties.bath_available} ${pointsLabel(s.sanitary && s.sanitary.bath_points)}`,
              '3': `${locales && locales.properties && locales.properties.separate_bath_and_shower_in_one_room} ${pointsLabel(s.sanitary && s.sanitary.shower_bath_points)}`,
            }">
        </theme-radios>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-4 gap-5 mb-10'>
        <div>
          <input type='hidden' name='sanitary[cupboard_with_built_in_washbasin]' value='0'/>
          <theme-checkboxes
            id='cupboard_with_built_in_washbasin'
            @input='countSanitaryPoints'
            label=''
            :is-single-checkbox='true'
            name='sanitary[cupboard_with_built_in_washbasin]'
            v-model='sanitary.cupboard_with_built_in_washbasin.value'
            :options="{ '1': `${locales && locales.properties && locales.properties.cupboard_with_built_in_washbasin} ${pointsLabel(s.sanitary && s.sanitary.cupboard_with_built_in_washbasin_points)}` }">
          </theme-checkboxes>
        </div>
        <div>
          <input type='hidden' name='sanitary[extra_for_a_bubble_bath]' value='0'/>
          <theme-checkboxes
            id='extra_for_a_bubble_bath'
            @input='countSanitaryPoints'
            label=''
            :is-single-checkbox='true'
            name='sanitary[extra_for_a_bubble_bath]'
            v-model='sanitary.extra_for_a_bubble_bath.value'
            :options="{ '1': `${locales && locales.properties && locales.properties.extra_for_a_bubble_bath} ${pointsLabel(s.sanitary && s.sanitary.extra_for_a_bubble_bath_points)}` }">
          </theme-checkboxes>
        </div>
        <div>
          <input type='hidden' name='sanitary[mounted_complete_separation_of_shower]' value='0'/>
          <theme-checkboxes
            id='mounted_complete_separation_of_shower'
            @input='countSanitaryPoints'
            label=''
            :is-single-checkbox='true'
            name='sanitary[mounted_complete_separation_of_shower]'
            v-model='sanitary.mounted_complete_separation_of_shower.value'
            :options="{ '1': `${locales && locales.properties && locales.properties.mounted_complete_separation_of_shower} ${pointsLabel(s.sanitary && s.sanitary.mounted_complete_separation_of_shower_points)}` }">
          </theme-checkboxes>
        </div>
        <div>
          <input type='hidden' name='sanitary[towel_radiator]' value='0'/>
          <theme-checkboxes
            id='towel_radiator'
            @input='countSanitaryPoints'
            label=''
            :is-single-checkbox='true'
            name='sanitary[towel_radiator]'
            v-model='sanitary.towel_radiator.value'
            :options="{ '1': `${locales && locales.properties && locales.properties.towel_radiator} ${pointsLabel(s.sanitary && s.sanitary.towel_radiator_points)}` }">
          </theme-checkboxes>
        </div>
        <div>
          <input type='hidden' name='sanitary[power_outlet]' value='0'/>
          <theme-checkboxes
            id='power_outlet'
            @input='countSanitaryPoints'
            label=''
            :is-single-checkbox='true'
            name='sanitary[power_outlet]'
            v-model='sanitary.power_outlet.value'
            :options="{ '1': `${locales && locales.properties && locales.properties.power_outlet} ${pointsLabel(s.sanitary && s.sanitary.power_outlet_points)}` }">
          </theme-checkboxes>
        </div>
        <div>
          <input type='hidden' name='sanitary[extra_bathroom_cupboard]' value='0'/>
          <theme-checkboxes
            id='extra_bathroom_cupboard'
            @input='countSanitaryPoints'
            label=''
            :is-single-checkbox='true'
            name='sanitary[extra_bathroom_cupboard]'
            v-model='sanitary.extra_bathroom_cupboard.value'
            :options="{ '1': `${locales && locales.properties && locales.properties.extra_bathroom_cupboard} ${pointsLabel(s.sanitary && s.sanitary.extra_bathroom_cupboard_points)}` }">
          </theme-checkboxes>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-4 gap-5 mb-10 items-end'>
        <div>
          <theme-numeric
            v-model='sanitary.luxury_mixer_tap.value'
            valid-scope='modal-form'
            name='sanitary[luxury_mixer_tap]'
            :label="`${locales && locales.properties && locales.properties.luxury_mixer_tap} ${pointsLabel(s.sanitary && s.sanitary.luxury_mixer_tap_points)} ${sanitary.luxury_mixer_tap.value ? sanitary.luxury_mixer_tap.total : ''}`"
            :precision='0'
            @input='countSanitaryPoints'>
          </theme-numeric>
        </div>
        <div>
          <theme-numeric
            v-model='sanitary.thermostatic_water_mixer.value'
            valid-scope='modal-form'
            name='sanitary[thermostatic_water_mixer]'
            :label="`${locales && locales.properties && locales.properties.thermostatic_water_mixer} ${pointsLabel(s.sanitary && s.sanitary.thermostatic_water_mixer_points)} ${sanitary.thermostatic_water_mixer.value ? sanitary.thermostatic_water_mixer.total : ''}`"
            :precision='0'
            @input='countSanitaryPoints'>
          </theme-numeric>
        </div>
        <div v-if="sanitary.power_outlet.value > 0">
          <theme-numeric
            v-model='sanitary.number_of_power_outlet.value'
            valid-scope='modal-form'
            name='sanitary[number_of_power_outlet]'
            :label="`${locales && locales.properties && locales.properties.number_of_power_outlet} ${sanitary.number_of_power_outlet.value ? sanitary.power_outlet.total : ''}`"
            :precision='0'
            @input='countSanitaryPoints'>
          </theme-numeric>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='sanitary.total'
            valid-scope='modal-form'
            name='sanitary[total]'
            :label='locales && locales.properties && locales.properties.total_sanitary'
            :precision='2'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.housing_for_disabled }}
        <small class='calculate-price__pts-hint' v-if='s.disabled && s.disabled.total_investment_points'>{{ formatPts(s.disabled.total_investment_points) }} pt / €1000</small>
      </h2>
      <div class='grid grid-cols-1 lg:grid-cols-4 gap-5 mb-10 items-end'>
        <div>
          <theme-numeric
            v-model='disabled.total_investment.value'
            valid-scope='modal-form'
            name='disabled[total_investment]'
            :label='locales && locales.properties && locales.properties.disabled_total_investment'
            @input='countDisabledPoints'
            :precision='2'>
          </theme-numeric>
        </div>
        <div>
          <theme-numeric
            v-model='disabled.disabled_subsidy.value'
            valid-scope='modal-form'
            name='disabled[subsidy]'
            :label='locales && locales.properties && locales.properties.disabled_investment_subsidy'
            @input='countDisabledPoints'
            :precision='2'>
          </theme-numeric>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='disabled.total'
            valid-scope='modal-form'
            name='disabled[total]'
            :label='locales && locales.properties && locales.properties.total_housing_for_disabled'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.shared_parking }}
        <small class='calculate-price__pts-hint' v-if='maxParkingPts'>tot {{ formatPts(maxParkingPts) }} pt · {{ locales && locales.properties && locales.properties.divided_by_addresses }}</small>
      </h2>
      <div class='mb-10'>
        <h3 class='calculate-price__form-subtitle'>
          {{ locales && locales.properties && locales.properties.shared_parking }}
        </h3>
        <theme-radios
          v-model='sharedParking.type'
          name='shared_parking[type]'
          :options='sharedParkingOptionsWithPoints'
          @input='countSharedParkingPoints'>
        </theme-radios>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-4 mb-10'>
        <div>
          <theme-numeric
            v-model='sharedParking.number_of_addresses'
            valid-scope='modal-form'
            name='shared_parking[number_of_addresses]'
            :label='locales && locales.properties && locales.properties.number_of_addresses'
            @input='countSharedParkingPoints'>
          </theme-numeric>
        </div>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='sharedParking.total'
            valid-scope='modal-form'
            name='shared_parking[total]'
            :label='locales && locales.properties && locales.properties.total_shared_parking'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.private_outdoor_space }}
        <small class='calculate-price__pts-hint' v-if='s.outdoor && s.outdoor.max_points'>tot {{ formatPts(s.outdoor.max_points) }} pt · {{ formatPts(s.outdoor.space_points) }} pt/m²</small>
      </h2>

      <!-- Facility-based outdoor spaces (when property has outdoor space facilities with category) -->
      <template v-if='outdoor.spaces && outdoor.spaces.length'>
        <div v-for='(space, idx) in outdoor.spaces' :key='idx' class='mb-8 border-b pb-6'>
          <h3 class='calculate-price__form-subtitle mb-3'>
            <template v-if='!space.is_extra'>
              {{ localizedName(space) }}
            </template>
            <template v-else>
              <select
                v-model='outdoor.spaces[idx].facility_metadata_id'
                @change='onOutdoorSpaceSelect(idx)'
                class='calculate-price__room-type-select'>
                <option value=''>-- {{ locales && locales.properties && locales.properties.select_outdoor_space_type }} --</option>
                <option v-for='f in (facilityOptionsByCategory.outdoor_space || [])' :key='f.id' :value='f.id'>{{ localizedName(f) }}</option>
              </select>
            </template>
          </h3>
          <input type='hidden' :name='`outdoor[spaces][` + idx + `][name_nl]`' :value='space.name_nl || ""'/>
          <input type='hidden' :name='`outdoor[spaces][` + idx + `][name_en]`' :value='space.name_en || ""'/>
          <div class='mb-3'>
            <theme-radios
              :value="outdoor.spaces[idx].is_private == 1 ? 'private' : outdoor.spaces[idx].is_common == 1 ? 'common' : null"
              @input="setOutdoorSpaceType(idx, $event)"
              :name='`outdoor_space_type_${idx}`'
              :options="{
                'private': `${locales && locales.properties && locales.properties.private_outdoor_space} (${pointsLabel(s.outdoor && s.outdoor.space_points, {suffix: locales && locales.properties && locales.properties.per_m2, wrap: false})}, ${pointsLabel(s.outdoor && s.outdoor.min_space_points, {wrap: false})} ${(locales && locales.properties && locales.properties.outdoor_bonus_once_note) || 'once, not per space'})`,
                'common': `${locales && locales.properties && locales.properties.common_outdoor_space} (${pointsLabel(s.outdoor && s.outdoor.common_space_points, {suffix: locales && locales.properties && locales.properties.per_m2, wrap: false})}, ${(locales && locales.properties && locales.properties.divided_by_addresses) || 'divided by addresses'})`
              }">
            </theme-radios>
            <input type='hidden' :name='`outdoor[spaces][` + idx + `][is_private]`' :value='outdoor.spaces[idx].is_private'/>
            <input type='hidden' :name='`outdoor[spaces][` + idx + `][is_common]`' :value='outdoor.spaces[idx].is_common'/>
          </div>
          <div class='grid grid-cols-1 lg:grid-cols-3 gap-5 items-end'>
            <div>
              <theme-numeric
                v-model='outdoor.spaces[idx].area'
                valid-scope='modal-form'
                :name='`outdoor[spaces][` + idx + `][area]`'
                :label="`${locales && locales.properties && locales.properties.private_outdoor_space}`"
                :precision='2'
                @input='countOutdoorPoints'
                :IsExtraInfo='true'
                :extra-info-suffix='true'>
                <template slot='extra-info'> m<sup>2</sup></template>
              </theme-numeric>
            </div>
            <div v-if='outdoor.spaces[idx].is_common == 1'>
              <theme-numeric
                v-model='outdoor.spaces[idx].shared_addresses'
                valid-scope='modal-form'
                :name='`outdoor[spaces][` + idx + `][shared_addresses]`'
                :label="locales && locales.properties && locales.properties.common_outdoor_space_addresses "
                :precision='0'
                @input='countOutdoorPoints'>
              </theme-numeric>
            </div>
          </div>
        </div>
      </template>

      <!-- Legacy flat outdoor fields (when no facility-based outdoor spaces) -->
      <template v-else>
        <div class='grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10 items-end'>
          <div>
            <theme-numeric
              v-model='outdoorSpacesArea'
              valid-scope='modal-form'
              name='outdoor[space_area]'
              :label="`${locales && locales.properties && locales.properties.private_outdoor_space}`"
              :precision='2'
              @input='countOutdoorPoints'
              :IsExtraInfo='true'
              :extra-info-suffix='true'>
              <template slot='extra-info'> m<sup>2</sup></template>
            </theme-numeric>
          </div>
          <div>
            <theme-numeric
              v-model='outdoorCommonSpacesArea'
              valid-scope='modal-form'
              name='outdoor[common_space_area]'
              :label="`${locales && locales.properties && locales.properties.common_outdoor_space}`"
              :precision='2'
              @input='countOutdoorPoints'
              :IsExtraInfo='true'
              :extra-info-suffix='true'>
              <template slot='extra-info'> m<sup>2</sup></template>
            </theme-numeric>
          </div>
          <div>
            <theme-numeric
              v-model='outdoorCommonSpacesAddresses'
              valid-scope='modal-form'
              name='outdoor[common_space_addresses]'
              :label="`${locales && locales.properties && locales.properties.common_outdoor_space_addresses}`"
              :precision='0'
              @input='countOutdoorPoints'>
            </theme-numeric>
          </div>
        </div>
      </template>

      <div class='mb-5'>
        <button type='button' class='calculate-price__add-room-btn' @click='addOutdoorSpace'>
          + {{ locales && locales.properties && locales.properties.add_room  }}
        </button>
      </div>

      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='outdoor.total'
            valid-scope='modal-form'
            name='outdoor[total]'
            :label='locales && locales.properties && locales.properties.total_outdoor'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.vop }}
        ({{
          locales &&
          locales.common &&
          locales.common.minimum + ' € ' + independantMinVopPrice
        }})
      </h2>
      <div class='grid grid-cols-1 lg:grid-cols-4 mb-10'>
        <div>
          <theme-numeric
            v-model='vop.price.value'
            valid-scope='modal-form'
            name='vop[price]'
            :label='locales && locales.properties && locales.properties.enter_vop'
            @input='countVopPoints'>
          </theme-numeric>
        </div>
      </div>
      <!-- 40-point WOZ minimum for 2015-2019 newbuild / high renovation (official
           rule condition 1+2 - condition 3, other rubrics >= 110, is checked
           separately in countVopPoints). Applies uniformly, not just 2026+. -->
      <div class='mb-10' v-if='new Date(independantSettings.up_to_date) >= newVersion'>
        <input type='hidden' name='vop[built_between_2015_and_2019]' value='0'/>
        <theme-checkboxes
          id='built_between_2015_and_2019'
          @input='countVopPoints'
          label=''
          :is-single-checkbox='true'
          name='vop[built_between_2015_and_2019]'
          v-model='vop.built_between_2015_and_2019.value'
          :options="{ '1': `${(locales && locales.properties && locales.properties.built_between_2015_and_2019) || 'Newbuild or high-level renovation'} (${pointsLabel(s.vop_nieuwbouw_floor_points || 40, {wrap: false})} ${locales && locales.properties && locales.properties.woz_minimum_applies_when_other_rubrics_at_least} ${formatPts(s.vop_nieuwbouw_other_min || 110)})` }">
        </theme-checkboxes>
        <template v-if='vop.built_between_2015_and_2019.value == 1'>
          <input type='hidden' name='vop[built_between_2015_and_2019]' value='1'/>
        </template>
        <div class='grid grid-cols-1 lg:grid-cols-4 mt-4' v-if='vop.built_between_2015_and_2019.value == 1'>
          <div>
            <theme-numeric
              v-model='vop.nieuwbouw_year.value'
              valid-scope='modal-form'
              name='vop[nieuwbouw_year]'
              :label='locales && locales.properties && locales.properties.vop_nieuwbouw_year || "Construction/renovation year"'
              @input='countVopPoints'>
            </theme-numeric>
          </div>
        </div>
      </div>
      <div class='mb-10'>
        <input
          type='hidden'
          name='vop[newly_built_renovated_house]'
          value='0'
        />
        <theme-checkboxes
          id='newly_built_renovated_house'
          @input='countVopPoints'
          label=''
          :is-single-checkbox='true'
          name='vop[newly_built_renovated_house]'
          v-model='vop.newly_built_renovated_house.value'
          :options="{ '1': locales &&locales.properties && locales.properties.newly_built_renovated_house }">
        </theme-checkboxes>
      </div>
      <div class='mb-10' v-if='vop.newly_built_renovated_house.value'>
        <h3 class='calculate-price__form-subtitle'>
          {{ locales && locales.properties && locales.properties.delivery_in }}
        </h3>
        <theme-radios
          v-model='vop.delivery.value'
          name='vop[delivery]'
          :options="{
              2018: '2018',
              2019: '2019',
              2020: '2020',
              2021: '2021',
              2022: '2022',
            }"
          @input='countVopPoints'>
        </theme-radios>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-4 mb-10' v-if='vop.delivery.value > 2017'>
        <div>
          <theme-autocomplete-custom
            :options='muncipalities'
            :initial='vop.location.value'
            v-model='vop.location.value'
            name='vop[location]'
            @input='countVopPoints'
            :label='locales && locales.properties && locales.properties.muncipality'>
          </theme-autocomplete-custom>
        </div>
      </div>
      <div class='grid grid-cols-2 lg:grid-cols-3 mb-10'>
        <div>
          <h3 class='calculate-price__form-subtitle'>
            {{ locales && locales.properties && locales.properties.vop_absolute }}
          </h3>
          {{ vop.absolute }}
        </div>
        <div>
          <h3 class='calculate-price__form-subtitle'>
            {{ locales && locales.properties && locales.properties.vop_surface_proportion }}
          </h3>
          {{ vop.surface_proportion }}
        </div>
      </div>
      <div class='calculate-price__formula-box mb-10'>
        <h3 class='calculate-price__form-subtitle'>
          {{ locales && locales.properties && locales.properties.vop_formula_title }}
        </h3>
        <p>
          {{ locales && locales.properties && locales.properties.vop_formula_used_value }}:
          € {{ formatPts(vop.priceUsed) }}<template v-if='vop.priceWasMinimum'> ({{ locales && locales.properties && locales.properties.vop_formula_minimum_applied }})</template>
        </p>
        <p>
          {{ locales && locales.properties && locales.properties.vop_absolute }} =
          € {{ formatPts(vop.priceUsed) }} / {{ formatPts(vop.divisorAbsolute) }} = {{ vop.absolute }}
        </p>
        <p>
          {{ locales && locales.properties && locales.properties.vop_surface_proportion }} =
          (€ {{ formatPts(vop.priceUsed) }} / {{ vopSurface }} m²) / {{ formatPts(vop.divisorSurface) }} = {{ vop.surface_proportion }}
        </p>
        <p>
          {{ locales && locales.properties && locales.properties.vop_formula_used_surface }}: {{ vopSurface }} m²
        </p>
        <p>
          {{ locales && locales.properties && locales.properties.vop_formula_raw_total || 'Raw WOZ total' }}: {{ vop.rawTotal }}
        </p>
        <p>
          {{ locales && locales.properties && locales.properties.vop_formula_total }}: {{ vop.roundedTotal }}
        </p>
        <template v-if='vop.minimumApplied'>
          <p class='calculate-price__formula-highlight'>
            {{ locales && locales.properties && locales.properties.vop_formula_minimum_applied_label || 'Minimum applied' }}: {{ vop.total }}
          </p>
          <p>
            {{ locales && locales.properties && locales.properties.vop_formula_minimum_reason || 'Reason' }}:
            {{ locales && locales.properties && locales.properties['vop_minimum_reason_' + vop.minimumReason] || 'Qualifying construction/renovation rule (2015–2019) and other rubrics ≥ 110 points threshold met' }}
          </p>
        </template>
        <template v-if='vop.capped'>
          <p class='calculate-price__formula-highlight'>
            {{ locales && locales.properties && locales.properties.vop_formula_cap_applied_label || 'Cap applied' }}: {{ vop.total }}
          </p>
        </template>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='vop.total'
            valid-scope='modal-form'
            name='vop[total]'
            :label='locales && locales.properties && locales.properties.total_vop'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <!-- Special Features (Rubriek 12) — 2026+ only -->
    <div class='calculate-price__form-section' v-if='new Date(independantSettings.up_to_date) >= newVersion'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.special_features || 'Special features (Rubriek 12)' }}
        <small class='calculate-price__pts-hint' v-if='s.special_features && s.special_features.monument_national_percentage'>monument +{{ formatPts(s.special_features.monument_national_percentage) }}%</small>
      </h2>

      <!-- Video/audio doorbell -->
      <div class='mb-5'>
        <input type='hidden' name='special_features[video_phone]' value='0'/>
        <theme-checkboxes
          id='video_phone'
          @input='countSpecialFeaturesPoints'
          label=''
          :is-single-checkbox='true'
          name='special_features[video_phone]'
          v-model='specialFeatures.video_phone.value'
          :options="{ '1': `${(locales && locales.properties && locales.properties.video_phone) || 'Video/audio doorbell with remote door-opening'} ${pointsLabel(s.special_features && s.special_features.video_phone_points || 0.25)}` }">
        </theme-checkboxes>
        <template v-if='specialFeatures.video_phone.value == 1'>
          <input type='hidden' name='special_features[video_phone]' value='1'/>
        </template>
      </div>

      <!-- Exclusive EV charging station -->
      <div class='mb-5'>
        <input type='hidden' name='special_features[charging_station]' value='0'/>
        <theme-checkboxes
          id='charging_station'
          @input='countSpecialFeaturesPoints'
          label=''
          :is-single-checkbox='true'
          name='special_features[charging_station]'
          v-model='specialFeatures.charging_station.value'
          :options="{ '1': `${(locales && locales.properties && locales.properties.charging_station) || 'Exclusive EV charging station for residents'} ${pointsLabel(s.special_features && s.special_features.charging_station_points || 2)}` }">
        </theme-checkboxes>
        <template v-if='specialFeatures.charging_station.value == 1'>
          <input type='hidden' name='special_features[charging_station]' value='1'/>
        </template>
      </div>

      <!-- Newbuild surcharge -->
      <div class='mb-5'>
        <input type='hidden' name='special_features[new_build]' value='0'/>
        <theme-checkboxes
          id='new_build'
          @input='countSpecialFeaturesPoints'
          label=''
          :is-single-checkbox='true'
          name='special_features[new_build]'
          v-model='specialFeatures.new_build.value'
          :options="{ '1': `${(locales && locales.properties && locales.properties.new_build_surcharge) || 'Newbuild surcharge'} ${percentLabel(s.special_features && s.special_features.new_build_percentage || 10, {suffix: locales && locales.properties && locales.properties.percentage_of_rent})}` }">
        </theme-checkboxes>
        <p v-if='specialFeatures.new_build.value == 1' class='text-xs text-gray-500 mt-1'>
          {{ locales && locales.properties && locales.properties.new_build_surcharge_hint || 'Surcharge is applied to the final max rent and shown in the price summary below.' }}
        </p>
        <template v-if='specialFeatures.new_build.value == 1'>
          <input type='hidden' name='special_features[new_build]' value='1'/>
        </template>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5' v-if='specialFeatures.new_build.value == 1'>
        <div>
          <theme-calendar
            v-model='specialFeatures.new_build_first_use_date.value'
            name='special_features[new_build_first_use_date]'
            :label='locales && locales.properties && locales.properties.new_build_first_use_date || "First use date"'
            :date-mask='true'
            :past-dates='true'
            @input='countSpecialFeaturesPoints'>
          </theme-calendar>
        </div>
        <div>
          <input type='hidden' name='special_features[construction_started_before_2028]' value='1'/>
          <theme-checkboxes
            id='construction_started_before_2028'
            @input='countSpecialFeaturesPoints'
            label=''
            :is-single-checkbox='true'
            name='special_features[construction_started_before_2028]'
            v-model='specialFeatures.construction_started_before_2028.value'
            :options="{ '1': locales && locales.properties && locales.properties.construction_started_before_2028 || 'Construction/transformation started before 1 Jan 2028' }">
          </theme-checkboxes>
        </div>
      </div>

      <!-- Special features points total -->
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='specialFeatures.total'
            valid-scope='modal-form'
            name='special_features[total]'
            :label='locales && locales.properties && locales.properties.total_special_features || "Special features total"'
            :precision='2'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-section'>
      <h2 class='calculate-price__form-title'>
        {{ locales && locales.properties && locales.properties.care_home }}
        <small class='calculate-price__pts-hint' v-if='s.care_home && s.care_home.points'>{{ percentLabel(s.care_home.points, {wrap: false}) }} {{ locales && locales.properties && locales.properties.percentage_of_subtotal }}</small>
      </h2>
      <div class='mb-10'>
        <input type='hidden' name='care_home[value]' value='0'/>
        <theme-checkboxes
          id='care_home'
          @input='countCareHomePoints'
          label=''
          :is-single-checkbox='true'
          name='care_home[value]'
          v-model='careHome.value'
          :options="{ '1': `${locales && locales.properties && locales.properties.care_home} ${percentLabel(s.care_home && s.care_home.points, {suffix: locales && locales.properties && locales.properties.percentage_of_subtotal})}` }">
        </theme-checkboxes>
      </div>
      <div class='grid grid-cols-1 lg:grid-cols-3'>
        <div>
          <theme-numeric
            v-model='careHome.total'
            valid-scope='modal-form'
            name='care_home[total]'
            :label='locales && locales.properties && locales.properties.total_care_home'
            :isReadonly='true'>
          </theme-numeric>
        </div>
      </div>
    </div>

    <div class='calculate-price__form-bottom'>
      <div class='default-content grid grid-cols-1 lg:grid-cols-12 items-center gap-1 lg:gap-5'>
        <div class='col-span-2 hidden lg:block'>
          <h3 class='calculate-price__form-bottom-title'>
            {{ this.locales && this.locales.common && this.locales.common.total }}
          </h3>
        </div>
        <div class='lg:col-span-7'>
          <div class='grid grid-cols-2 items-center gap-5'>
            <div class='grid lg:grid-cols-2 items-center gap-1 lg:gap-5 lg:pr-6'>
              <div class='calculate-price__form-bottom-label'>
                {{ locales && locales.properties && locales.properties.total_points }}
              </div>
              <div class='lg:hidden'>{{ totalPoints || '0' }}</div>
              <div class='hidden lg:block'>
                <theme-numeric
                  v-model='totalPoints'
                  valid-scope='modal-form'
                  name='total'
                  label=''
                  :isReadonly='true'>
                </theme-numeric>
              </div>
            </div>
            <div class='grid lg:grid-cols-2 items-center gap-1 lg:gap-5 lg:pr-6'>
              <div class='calculate-price__form-bottom-label'>
                {{ locales && locales.properties && locales.properties.max_basic_rent_price }}
              </div>
              <div class='lg:hidden'>{{ price || '0' }}</div>
              <div class='hidden lg:block'>
                <theme-numeric
                  v-model='price'
                  valid-scope='modal-form'
                  name='price'
                  label=''
                  :isReadonly='true'>
                </theme-numeric>
              </div>
            </div>
          </div>
          <!-- Surcharge breakdown (2026+ only, when any surcharge applies) -->
          <div class='grid grid-cols-2 gap-1 lg:gap-5 mt-2' v-if='new Date(independantSettings.up_to_date) >= newVersion && specialFeatures.surchargeAmount > 0'>
            <div class='grid lg:grid-cols-2 items-center gap-1 lg:gap-5 lg:pr-6'>
              <div class='calculate-price__form-bottom-label'>
                {{ locales && locales.properties && locales.properties.base_rent || 'Base rent' }}
              </div>
              <div>{{ specialFeatures.basePrice || '0' }}</div>
            </div>
            <div class='grid lg:grid-cols-2 items-center gap-1 lg:gap-5 lg:pr-6'>
              <div class='calculate-price__form-bottom-label'>
                {{ locales && locales.properties && locales.properties.surcharge || 'Surcharge' }}
                ({{ specialFeatures.surchargePct || 0 }}%)
              </div>
              <div>+ {{ specialFeatures.surchargeAmount || '0' }}</div>
            </div>
          </div>
        </div>
        <div class='lg:col-span-3 grid grid-cols-2 items-center gap-5'>
          <button type='button' class='theme-btn theme-btn--theme' :disabled='downloading' @click='downloadPdf'>
            {{ downloading
              ? (locales && locales.common && locales.common.generating_pdf)
              : (locales && locales.common && locales.common.download_pdf) }}
          </button>
          <button type='submit' name='submit' value='save' class='theme-btn theme-btn--theme'>
            {{ locales && locales.common && locales.common.save }}
          </button>
        </div>
        <div v-if='refreshUrl' class='hidden lg:block lg:col-span-3 mt-3'>
          <a href='#' class='theme-btn theme-btn--secondary' @click.prevent='confirmRefresh'>
            ↺ {{ locales && locales.properties && locales.properties.refresh_from_property }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex';
  import _ from 'lodash';
  import sweetalert from 'sweetalert';

  export default {
    computed: {
      ...mapGetters(['metadata', 'locales']),
      // Same convention already used elsewhere (app.js, VideoPlayer.vue): the current
      // display locale is exposed once on <body data-lang="..."> by the layout, driven by
      // Config::get('app.locale') server-side — never re-derived or passed as a prop.
      currentLocale() {
        var body = document.querySelector('body');
        return (body && body.getAttribute('data-lang')) || 'nl';
      },
      hasFacilityRooms() {
        return !!(this.propertyFacilityMap && Object.keys(this.propertyFacilityMap).length > 0);
      },
      s() {
        return (this.independantSettings && this.independantSettings.price_points_settings) || {};
      },
      maxEnergyPts() {
        var energy = this.s && this.s.energy;
        if (!energy) return null;
        var key = (this.energy && this.energy.property_type == 2) ? 'multi_family' : 'single_family';
        var max = -Infinity;
        Object.keys(energy).forEach(function(k) {
          var e = energy[k];
          if (e && typeof e === 'object' && e[key] !== undefined) {
            var v = parseFloat(String(e[key]).replace(',', '.'));
            if (!isNaN(v) && v > max) max = v;
          }
        });
        return max > -Infinity ? max : null;
      },
      /**
       * energyLabelsCustomOrder (server-built [id, label] pairs) drives the
       * radios' actual rendering when sort-by="custom" - ThemeRadios reads
       * customOrder directly and ignores the `options` prop entirely in that
       * mode, so per-option point rates must be appended here, not on
       * energyLabelsOptions. Rate depends on the currently selected property
       * type (single/multi family) since that's the only surface-independent
       * part of the value available without a surface bracket.
       *
       * Unlike the dependent form, independent energy points are a flat
       * total for the label (settings.energy[label].single_family/
       * multi_family, optionally tiered by a surface bracket via the
       * _below25/_between_25_and_40 variants - see countEnergyPoints()) -
       * never multiplied by surface. No "per m²" suffix here.
       */
      energyLabelsCustomOrderWithPoints() {
        var pps = (this.s && this.s.energy) || {};
        var key = (this.energy && this.energy.property_type == 2) ? 'multi_family' : 'single_family';
        var self = this;
        return (this.energyLabelsCustomOrder || []).map(function (pair) {
          var id = pair[0];
          var label = pair[1];
          var rate = pps[id] && pps[id][key];
          if (rate === undefined || rate === null || rate === '') {
            return [id, label];
          }
          return [id, label + ' ' + self.pointsLabel(rate)];
        });
      },
      /**
       * Monument-type radio labels previously hardcoded their percentage/point
       * text directly in the translation fallback strings (e.g. "... (+15%)"),
       * which would silently go stale if special_features.* settings were
       * ever changed via the admin settings screen. Reads the live values
       * instead - options 3/9/7 are % surcharges on rent (calcSurchargesForPrice),
       * option 2 is a fixed legacy point add (countSpecialFeaturesPoints).
       */
      monumentTypeOptions() {
        var sf = (this.s && this.s.special_features) || {};
        var p = (this.locales && this.locales.properties) || {};
        return {
          '1': p.monument_none || 'Not a listed building',
          '3': `${p.monument_municipal || 'Municipal or provincial listed building'} ${this.percentLabel(sf.monument_municipal_provincial_percentage || 15)}`,
          '9': `${p.monument_townscape || 'Protected townscape or village'} ${this.percentLabel(sf.protected_view_percentage || 5)}`,
          '7': `${p.monument_national_post2024 || 'National monument, agreement on/after 1 July 2024'} ${this.percentLabel(sf.monument_national_percentage || 35)}`,
          '2': `${p.monument_national_pre2024 || 'National monument, agreement before 1 July 2024'} ${this.pointsLabel(50)}`,
        };
      },
      maxParkingPts() {
        var sp = this.s && this.s.shared_parking;
        if (!sp) return null;
        var max = 0;
        Object.keys(sp).forEach(function(k) {
          if (k !== 'ev_charging_points') {
            var v = parseFloat(String(sp[k]).replace(',', '.'));
            if (!isNaN(v) && v > max) max = v;
          }
        });
        return max > 0 ? max : null;
      },
      /**
       * sharedParkingOptions (prop, id-keyed labels from Metadata) enriched
       * with each type's point rate, divided-by-addresses rule note kept
       * separate (shown once via the section total, since it applies to
       * every option equally rather than being option-specific).
       */
      sharedParkingOptionsWithPoints() {
        var sp = (this.s && this.s.shared_parking) || {};
        var out = {};
        var self = this;
        Object.keys(this.sharedParkingOptions || {}).forEach(function (id) {
          var label = self.sharedParkingOptions[id];
          var rate = sp[id];
          out[id] = (rate !== undefined && rate !== null && rate !== '')
            ? label + ' ' + self.pointsLabel(rate)
            : label;
        });
        return out;
      },
    },
    data() {
    return {
      total: 0,
      totalPoints: 0,
      surface: 0,
      vopSurface: 0,
      price: 0,
      heatingTypeClone: this.heatingType,
      outdoorSpacesArea: 0,
      outdoorCommonSpacesArea: 0,
      outdoorCommonSpacesAddresses: 0,
      newVersion: new Date('2024-07-01'),
      localRooms: [],
      localOtherRooms: [],
      extraRoomCount: 0,
      extraOtherRoomCount: 0,
      downloading: false,
      downloadRequestSeq: 0,
    };
  },
  props: {
    property: {
      default: () => {
        return {};
      },
      type: Object,
    },
    energyLabelsOptions: {
      default: () => {
        return {};
      },
      type: Object,
    },
    energyLabelsCustomOrder: {
      default: () => {
        return {};
      },
      type: [Object, Array],
    },
    outdoorSpacesCustomOrder: {
      default: () => {
        return {};
      },
      type: [Object, Array],
    },
    energyLabels: {
      default: () => {
        return {};
      },
      type: Object,
    },
    energyLabelsBySlug: {
      default: () => {
        return {};
      },
      type: Object,
    },
    energyLabelsIdBySlug: {
      default: () => {
        return {};
      },
      type: Object,
    },
    outdoorSpaces: {
      default: () => {
        return {};
      },
      type: Object,
    },
    outdoorSpacesOptions: {
      default: () => {
        return {};
      },
      type: Object,
    },
    independantLandlordSpendingsBase: {
      default: 0,
      type: [String, Number],
    },
    independantMinVopPrice: {
      default: 0,
      type: [String, Number],
    },
    independantVopPointPrice: {
      default: 0,
      type: [String, Number],
    },
    independantVopProportionPrice: {
      default: 0,
      type: [String, Number],
    },
    independantVopProportionPriceAmsterdamUtrecht: {
      default: 0,
      type: [String, Number],
    },
    independantMaxRentalPrices: {
      default: () => {
        return {};
      },
      type: [Array, Object],
    },
    independantSettings: {
      default: () => {
        return {};
      },
      type: [Array, Object],
    },
    muncipalities: {
      default: () => {
        return [];
      },
      type: Array,
    },
    heatingType: {
      default: null,
      type: [String, Number],
    },
    sharedParking: {
      default: () => {
        return {};
      },
      type: Object,
    },
    leaveArea: {
      default: () => {
        return {};
      },
      type: Object,
    },
    otherSpaces: {
      default: () => {
        return {};
      },
      type: Object,
    },
    heating: {
      default: () => {
        return {};
      },
      type: Object,
    },
    energy: {
      default: () => {
        return {};
      },
      type: Object,
    },
    kitchen: {
      default: () => {
        return {};
      },
      type: Object,
    },
    sanitary: {
      default: () => {
        return {};
      },
      type: Object,
    },
    disabled: {
      default: () => {
        return {};
      },
      type: Object,
    },
    outdoor: {
      default: () => {
        return {};
      },
      type: Object,
    },
    vop: {
      default: () => {
        return {};
      },
      type: Object,
    },
    renovation: {
      default: () => {
        return {};
      },
      type: Object,
    },
    careHome: {
      default: () => {
        return {};
      },
      type: Object,
    },
    monumentAllowance: {
      default: () => {
        return {};
      },
      type: Object,
    },
    specialFeatures: {
      default: () => {
        return {};
      },
      type: Object,
    },
    facilityTypes: {
      default: () => {
        return {};
      },
      type: Object,
    },
    propertyFacilityMap: {
      default: () => {
        return {};
      },
      type: Object,
    },
    facilityOptionsByCategory: {
      default: () => {
        return { room: [], other_room: [], outdoor_space: [] };
      },
      type: Object,
    },
    energyLabels: {
      default: () => {
        return {};
      },
      type: Object,
    },
    rooms: {
      default: () => {
        return [];
      },
      type: Array,
    },
    otherRooms: {
      default: () => {
        return [];
      },
      type: Array,
    },
    investmentsDate: {
      default: "",
      type: String,
    },
    investmentsYear: {
      default: "",
      type: String,
    },
    sharedParkingOptions: {
      default: () => {
        return {};
      },
      type: Object,
    },
    refreshUrl: {
      default: '',
      type: String,
    },
  },
  beforeMount() {
    // Initialise reactive specialFeatures sub-objects from saved prop data
    if (!this.specialFeatures.monument_type) Vue.set(this.specialFeatures, 'monument_type', { value: 1 });
    if (!this.specialFeatures.video_phone) Vue.set(this.specialFeatures, 'video_phone', { value: 0 });
    if (!this.specialFeatures.charging_station) Vue.set(this.specialFeatures, 'charging_station', { value: 0 });
    if (!this.specialFeatures.new_build) Vue.set(this.specialFeatures, 'new_build', { value: 0 });
    if (!this.specialFeatures.new_build_first_use_date) Vue.set(this.specialFeatures, 'new_build_first_use_date', { value: '' });
    if (!this.specialFeatures.construction_started_before_2028) Vue.set(this.specialFeatures, 'construction_started_before_2028', { value: 1 });
    Vue.set(this.specialFeatures, 'total', 0);
    Vue.set(this.specialFeatures, 'surchargeAmount', 0);
    Vue.set(this.specialFeatures, 'surchargePct', 0);
    Vue.set(this.specialFeatures, 'surchargeBreakdown', {});
    Vue.set(this.specialFeatures, 'basePrice', 0);
    // Initialise vop.built_between_2015_and_2019 if not in saved data
    if (!this.vop.built_between_2015_and_2019) Vue.set(this.vop, 'built_between_2015_and_2019', { value: 0 });
    // Initialise vop.nieuwbouw_year if not in saved data (legacy calculations
    // saved before this field existed) - missing/blank means "cannot verify
    // the 2015-2019 window", not "assume it qualifies".
    if (!this.vop.nieuwbouw_year) Vue.set(this.vop, 'nieuwbouw_year', { value: '' });

    // Mirror prop arrays into local mutable copies so users can add extra rooms
    this.localRooms = this.rooms.slice();
    this.localOtherRooms = this.otherRooms.slice();

    // Defensive: also pick up any extra_*/extra_other_* keys present in the leaveArea/
    // otherSpaces data but not already in the rooms/otherRooms prop (the v-for template loop
    // renders localRooms/localOtherRooms, not Object.keys(leaveArea), so a saved dynamic room
    // only appears once its key is in this list — see PropertiesController::restoreDynamicRooms()
    // for the normal path that supplies it via the rooms/otherRooms props already).
    Object.keys(this.leaveArea).forEach(key => {
      if (key.indexOf('extra_') === 0 && this.localRooms.indexOf(key) === -1) {
        this.localRooms.push(key);
      }
    });
    Object.keys(this.otherSpaces).forEach(key => {
      if (key.indexOf('extra_other_') === 0 && this.localOtherRooms.indexOf(key) === -1) {
        this.localOtherRooms.push(key);
      }
    });

    // Resume the extra-room counters from the highest restored suffix, so clicking
    // "+ Ruimte toevoegen" after reload can't collide with (and overwrite) a restored room.
    this.extraRoomCount = this.highestExtraSuffix(this.localRooms, /^extra_(\d+)$/);
    this.extraOtherRoomCount = this.highestExtraSuffix(this.localOtherRooms, /^extra_other_(\d+)$/);

    // Ensure per-room heated/cooled are reactive for all rooms
    this.localRooms.forEach(room => {
      if (this.leaveArea[room]) {
        if (this.leaveArea[room].heated === undefined) Vue.set(this.leaveArea[room], 'heated', 0);
        if (this.leaveArea[room].cooled === undefined) Vue.set(this.leaveArea[room], 'cooled', 0);
      }
    });
    this.localOtherRooms.forEach(room => {
      if (this.otherSpaces[room]) {
        if (this.otherSpaces[room].heated === undefined) Vue.set(this.otherSpaces[room], 'heated', 0);
        if (this.otherSpaces[room].cooled === undefined) Vue.set(this.otherSpaces[room], 'cooled', 0);
      }
    });

    // Ensure outdoor.spaces is reactive
    if (!this.outdoor.spaces) Vue.set(this.outdoor, 'spaces', null);

    this.countSurface();
    this.countLeaveAreaHeatedCooled();
    this.countHeatingPoints();
    this.countEnergyPoints();
    this.countKitchenPoints();
    this.countSanitaryPoints();
    this.countDisabledPoints();
    this.countOutdoorPoints();
    this.countSharedParkingPoints();
    this.countVopPoints();
    this.countSpecialFeaturesPoints();
    this.countCareHomePoints();
    this.countMonumentAllowancePoints();
  },
  methods: {
    roundToQuarterPoint(val) {
      return Math.floor((val * 4) + 0.5) / 4;
    },
    // Locale-aware display name for a room/space/facility entry — never used for what gets
    // submitted (name_nl/name_en hidden inputs keep sending both, unconditionally, so the
    // bilingual data contract on the backend is untouched by which language is displayed).
    // Fallback order: requested-language name -> Dutch name -> any other available localized
    // name -> a slug/type fallback so a legacy row missing both never renders blank.
    localizedName(entity, fallback) {
      if (!entity) return fallback || '';
      var locale = this.currentLocale;
      // Metadata-model shape (facilityTypes[...]): value_lang.{locale} / value_lang.nl,
      // falling back to the raw (Dutch-by-convention) `value` column.
      if (entity.value_lang) {
        var localized = entity.value_lang[locale] || entity.value_lang.nl || entity.value_lang.en;
        if (localized) return localized;
      }
      var requested = entity['name_' + locale];
      if (requested) return requested;
      if (entity.name_nl) return entity.name_nl;
      if (entity.name_en) return entity.name_en;
      if (entity.value) return entity.value;
      return fallback || entity.slug || entity.wws_key || '';
    },
    confirmRefresh() {
      const msg = this.locales && this.locales.properties && this.locales.properties.refresh_from_property_confirm
        || 'This will overwrite unsaved form changes with the current property data. Continue?';
      if (window.confirm(msg)) {
        window.location.href = this.refreshUrl;
      }
    },
    // Highest numeric suffix among keys matching `pattern` (e.g. /^extra_(\d+)$/), or 0 if
    // none match — used to resume extraRoomCount/extraOtherRoomCount after a reload so the
    // next generated key can't collide with a restored room.
    highestExtraSuffix(keys, pattern) {
      var max = 0;
      keys.forEach(function(key) {
        var m = pattern.exec(key);
        if (m) {
          var num = parseInt(m[1], 10);
          if (!isNaN(num) && num > max) max = num;
        }
      });
      return max;
    },
    addRoom() {
      var key;
      do {
        key = 'extra_' + (++this.extraRoomCount);
      } while (this.leaveArea[key]);
      Vue.set(this.leaveArea, key, { width: '', length: '', surface: '', extra_dimensions: [], heated: 0, cooled: 0, name_nl: '', name_en: '', facility_metadata_id: '' });
      this.localRooms.push(key);
    },
    addOtherRoom() {
      var key;
      do {
        key = 'extra_other_' + (++this.extraOtherRoomCount);
      } while (this.otherSpaces[key]);
      Vue.set(this.otherSpaces, key, { width: '', length: '', surface: '', extra_dimensions: [], heated: 0, cooled: 0, name_nl: '', name_en: '', facility_metadata_id: '' });
      this.localOtherRooms.push(key);
    },
    setOutdoorSpaceType(idx, val) {
      this.outdoor.spaces[idx].is_private = val === 'private' ? 1 : 0;
      this.outdoor.spaces[idx].is_common  = val === 'common'  ? 1 : 0;
      this.countOutdoorPoints();
    },
    addOutdoorSpace() {
      if (!this.outdoor.spaces) Vue.set(this.outdoor, 'spaces', []);
      this.outdoor.spaces.push({ name_nl: '', name_en: '', is_extra: true, facility_metadata_id: '', is_private: 0, is_common: 0, area: '', shared_addresses: 1 });
      this.countOutdoorPoints();
    },
    onExtraRoomSelect(roomKey, category) {
      var data = category === 'other_room' ? this.otherSpaces : this.leaveArea;
      var id = parseInt(data[roomKey].facility_metadata_id);
      var options = (this.facilityOptionsByCategory && this.facilityOptionsByCategory[category]) || [];
      var found = options.find(function(f) { return parseInt(f.id) === id; });
      if (found) {
        Vue.set(data[roomKey], 'name_nl', found.name_nl);
        Vue.set(data[roomKey], 'name_en', found.name_en);
      }
    },
    onOutdoorSpaceSelect(idx) {
      var id = parseInt(this.outdoor.spaces[idx].facility_metadata_id);
      var options = (this.facilityOptionsByCategory && this.facilityOptionsByCategory.outdoor_space) || [];
      var found = options.find(function(f) { return parseInt(f.id) === id; });
      if (found) {
        Vue.set(this.outdoor.spaces[idx], 'name_nl', found.name_nl);
        Vue.set(this.outdoor.spaces[idx], 'name_en', found.name_en);
      }
    },
    countLeaveAreaHeatedCooled() {
      // Derive aggregate heating/cooling counts from per-room flags and update heating model
      var heatedRooms = 0;
      var cooledRooms = 0;
      var otherHeated = 0;
      var otherCooled = 0;

      this.localRooms.forEach(room => {
        if (this.leaveArea[room]) {
          if (parseInt(this.leaveArea[room].heated) == 1) heatedRooms++;
          if (parseInt(this.leaveArea[room].cooled) == 1) cooledRooms++;
        }
      });
      this.localOtherRooms.forEach(room => {
        if (this.otherSpaces[room]) {
          if (parseInt(this.otherSpaces[room].heated) == 1) otherHeated++;
          if (parseInt(this.otherSpaces[room].cooled) == 1) otherCooled++;
        }
      });

      Vue.set(this.heating, 'heated_rooms', heatedRooms);
      Vue.set(this.heating, 'cooled_rooms', cooledRooms);
      Vue.set(this.heating, 'other_rooms_with_heating', otherHeated);
      Vue.set(this.heating, 'other_rooms_with_cooling', otherCooled);
      this.countHeatingPoints();
    },
    sumExtraDimensions(rows) {
      if (!rows || !rows.length) return 0;
      var sum = 0;
      for (var i = 0; i < rows.length; i++) {
        var w = parseFloat((rows[i].width + "").replace(",", "."));
        var l = parseFloat((rows[i].length + "").replace(",", "."));
        if (isNaN(w)) w = 0;
        if (isNaN(l)) l = 0;
        sum += w * l;
      }
      return sum;
    },
    addExtraDimension(object, room) {
      if (!object[room].extra_dimensions) {
        Vue.set(object[room], "extra_dimensions", []);
      }
      object[room].extra_dimensions.push({ length: "", width: "" });
    },
    removeExtraDimension(object, room, idx, group) {
      object[room].extra_dimensions.splice(idx, 1);
      this.calculateSurface(object, room, group);
    },
    calculateSurface(object, room, group) {
      if(object && object[room]){
        var width = parseFloat((object[room].width + "").replace(",", "."));
        if(isNaN(width)){
          width = 0;
        }
        var length = parseFloat((object[room].length + "").replace(",", "."));
        if(isNaN(length)){
          length = 0;
        }
        var surface = width * length + this.sumExtraDimensions(object[room].extra_dimensions);

        Vue.set(object[room], "surface", surface);
        switch (group) {
          case "leaveArea":
            this.countLeaveAreaPoints(object);
            break;
          case "otherSpaces":
            this.countOtherSpacesPoints(object);
            break;
        }
      }
    },
    countLeaveAreaPoints(object) {
      // Huurcommissie method: round each room to 2 decimals, sum the rounded
      // rooms, then round the COMBINED total once to whole m² before applying
      // the points-per-m² multiplier (rounding each room to a whole number
      // first, as countSurface() still does for the Energy tiers, under-counts
      // fractional rooms that individually round down).
      var sum = 0;
      for (var ob in object) {
        if (ob != "total" && ob != "include_shower") {
          var room = object[ob];
          if (room.surface) {
            sum += Math.round(parseFloat((room.surface + "").replace(",", ".")) * 100) / 100;
          }
        }
      }
      var surface = Math.round(sum);
      var points = surface * parseFloat((this.independantSettings.price_points_settings.surface_points_per_m2 + "").replace(",", ".")); //1;
      var total = this.roundToQuarterPoint(points);
      Vue.set(object, "total", total);

      this.countSurface();
      // Rubriek 4 (Energy)'s tier (<25 / 25-40 / >=40 m²) reads this.surface, which
      // countSurface() above just changed — without recomputing it here, adding/resizing a
      // room (e.g. via "+ Ruimte toevoegen", which doesn't itself touch any energy field)
      // left this.energy.total stuck at whatever tier applied before the room change,
      // silently under/over-counting the client preview total until the user happened to
      // also touch an energy field directly.
      this.countEnergyPoints();
      this.countCareHomePoints();
      this.countTotal();
    },
    countOtherSpacesPoints(object) {
      // Same Huurcommissie combined-then-rounded-once method as countLeaveAreaPoints().
      var sum = 0;
      for (var ob in object) {
        if (ob != "total" && ob != "attic_no_staircase") {
          var room = object[ob];
          if (room.surface) {
            sum += Math.round(parseFloat((room.surface + "").replace(",", ".")) * 100) / 100;
          }
        }
      }
      var surface = Math.round(sum);
      var points = surface * parseFloat((this.independantSettings.price_points_settings.other_spaces_points_per_m2+'').replace(',', '.')); //0,75
      var total = this.roundToQuarterPoint(points);
      Vue.set(object, "total", total);

      this.countSurface();
      // See the matching comment in countLeaveAreaPoints() — otherSpaces also feeds
      // this.surface, which the Energy rubric's tier depends on.
      this.countEnergyPoints();
      this.countCareHomePoints();
      this.countTotal();
    },
    countSurface() {
      var sum = 0;
      // Rubriek 11 (WOZ) uses its own surface basis: sum the raw room surfaces
      // first, then round the combined total once, rather than rounding each
      // room individually (which is what `surface` does, used by Rubriek 1/2's
      // own point totals and by the Energy rubric's surface tiers).
      var vopSum = 0;
      for (var ob in this.leaveArea) {
        if (this.leaveArea[ob] && this.leaveArea[ob].surface) {
          var s = parseFloat((this.leaveArea[ob].surface + "").replace(",", "."));
          sum += Math.round(s);
          vopSum += s;
        }
      }

      for (var ob in this.otherSpaces) {
        if (this.otherSpaces[ob] && this.otherSpaces[ob].surface) {
          var s2 = parseFloat((this.otherSpaces[ob].surface + "").replace(",", "."));
          sum += Math.round(s2);
          vopSum += s2;
        }
      }
      this.surface = sum;
      this.vopSurface = Math.round(vopSum);
    },
    countHeatingPoints() {
      var sum = 0;
      var heatingPoints = 0;
      var coolingPoints = 0;
      var otherHeatingPoints = 0;
      var otherCoolingPoints = 0;
      var h = this.independantSettings.price_points_settings.heating || {};

      var heatedRooms = parseFloat((this.heating.heated_rooms || 0) + "");
      heatingPoints = heatedRooms * parseFloat((h.points_per_room+'').replace(',', '.'));
      sum += heatingPoints;
      var maxOtherPoints = parseFloat((h.max_other_heating_points+'').replace(',', '.'));
      var hp = parseFloat((this.heating.other_rooms_with_heating || 0) + "") * parseFloat((h.points_per_other_room_with_heating+'').replace(',', '.'));
      if (hp < maxOtherPoints) {
        otherHeatingPoints = hp;
      } else {
        otherHeatingPoints = maxOtherPoints;
      }
      sum += otherHeatingPoints;
      // 2026: eligible cooled rooms = min(heated, cooled) when cooling_overlap_only=1
      var coolingOverlapOnly = parseInt(h.cooling_overlap_only || 0);
      var cooledRoomsInput = parseFloat((this.heating.cooled_rooms || 0) + "");
      var eligibleCooled = coolingOverlapOnly ? Math.min(heatedRooms, cooledRoomsInput) : cooledRoomsInput;
      var rawCoolingPoints = eligibleCooled * parseFloat((h.points_per_cooled_room+'').replace(',','.'));
      var maxCooledRoomsPoints = parseFloat(((h.max_cooled_rooms_points || 9999) + '').replace(',', '.'));
      coolingPoints = Math.min(rawCoolingPoints, maxCooledRoomsPoints);
      sum += coolingPoints;
      var maxOtherCoolingPoints = parseFloat((h.max_other_cooling_points+'').replace(',', '.'));
      var cp = parseFloat((this.heating.other_rooms_with_cooling || 0) + "") * parseFloat((h.points_per_other_room_with_cooling+'').replace(',', '.'));
      if (cp < maxOtherCoolingPoints) {
        otherCoolingPoints = cp;
      } else {
        otherCoolingPoints = maxOtherCoolingPoints;
      }

      sum += otherCoolingPoints;
      var total = this.roundToQuarterPoint(sum);
      Vue.set(this.heating, "heated_rooms_points", heatingPoints);
      Vue.set(
        this.heating,
        "other_rooms_with_heating_points",
        otherHeatingPoints
      );
      Vue.set(this.heating, "cooled_rooms_points", coolingPoints);
      Vue.set(
        this.heating,
        "other_rooms_with_cooling_points",
        otherCoolingPoints
      );
      Vue.set(this.heating, "total", total);
      this.countCareHomePoints();
      this.countTotal();
    },
    countEnergyPoints() {
      // Regression: every single_family/multi_family branch below now falls through to
      // multi_family (the `else`) whenever property_type isn't exactly 1, matching
      // App\Services\Wws\Rulesets\AbstractIndependentRuleset::calcEnergy()'s
      // `$propType === 1 ? single_family : multi_family` exactly. It used to be an
      // `else if (property_type == 2)` with no further else, so a saved calculation whose
      // energy.property_type was never explicitly set to 1 or 2 (e.g. "0", the field's
      // unset default — the radio only offers 1/2, nothing selects 0) silently scored 0
      // energy points on the client while the authoritative server calculator — which has
      // no such gap — scored the real (non-zero) multi_family value for the exact same
      // saved data. Found via a real property (3283) whose client-preview total (234)
      // never matched the authoritative download/PDF total (277) even with zero edits.
      var sum = 0;
      var energyPoints = 0;
      if (this.energy.energy_performance_fee == 1) {
        if (this.energy.property_type == 1) {
          energyPoints = parseFloat((this.independantSettings.price_points_settings.energy.energy_compensation.single_family+'').replace(',', '.')); //32
        } else {
          energyPoints = parseFloat((this.independantSettings.price_points_settings.energy.energy_compensation.multi_family+'').replace(',', '.')); //28
        }
        sum += energyPoints;
      } else {        
        if(this.surface < 25 && this.energy.energy_label && this.energyLabels[this.energy.energy_label] && this.energyLabels[this.energy.energy_label]["slug"] != "no_label_or_index"){
          if (this.energy.property_type == 1) {
            energyPoints = parseFloat((this.independantSettings.price_points_settings.energy[this.energy.energy_label].single_family_below25+'').replace(',', '.'));
          } else {
            energyPoints = parseFloat((this.independantSettings.price_points_settings.energy[this.energy.energy_label].multi_family_below25+'').replace(',', '.'));
          }
        }else if(this.surface >= 25 && this.surface < 40 && this.energy.energy_label && this.energyLabels[this.energy.energy_label] && this.energyLabels[this.energy.energy_label]["slug"] != "no_label_or_index"){
          if (this.energy.property_type == 1) {
            energyPoints = parseFloat((this.independantSettings.price_points_settings.energy[this.energy.energy_label].single_family_between_25_and_40+'').replace(',', '.'));
          } else {
            energyPoints = parseFloat((this.independantSettings.price_points_settings.energy[this.energy.energy_label].multi_family_between_25_and_40+'').replace(',', '.'));
          }
        }else if(this.surface >= 40 && this.energy.energy_label && this.energyLabels[this.energy.energy_label] && this.energyLabels[this.energy.energy_label]["slug"] != "no_label_or_index"){
          if (this.energy.property_type == 1) {
            energyPoints = parseFloat((this.independantSettings.price_points_settings.energy[this.energy.energy_label].single_family+'').replace(',', '.'));
          } else {
            energyPoints = parseFloat((this.independantSettings.price_points_settings.energy[this.energy.energy_label].multi_family+'').replace(',', '.'));
          }
          // console.log(this.energy.energy_label, energyPoints);
        }else if (this.energy.energy_label && this.energyLabels[this.energy.energy_label] && this.energyLabels[this.energy.energy_label]["slug"] == "no_label_or_index" && this.energy.building_construction_year) {
          if (this.energy.building_construction_year <= 1976) {
            var labelG = this.energyLabelsIdBySlug["label_g"];
            if (this.energy.property_type == 1) {
              energyPoints = parseFloat((this.independantSettings.price_points_settings.energy[labelG].single_family+'').replace(',', '.'));
            } else {
              energyPoints = parseFloat((this.independantSettings.price_points_settings.energy[labelG].multi_family+'').replace(',', '.'));
            }
          } else if (this.energy.building_construction_year <= 1978) {
            var labelF = this.energyLabelsIdBySlug["label_f"];
            if (this.energy.property_type == 1) {
              energyPoints = parseFloat((this.independantSettings.price_points_settings.energy[labelF].single_family+'').replace(',', '.'));
            } else {
              energyPoints = parseFloat((this.independantSettings.price_points_settings.energy[labelF].multi_family+'').replace(',', '.'));
            }
          } else if (this.energy.building_construction_year <= 1983) {
            var labelE = this.energyLabelsIdBySlug["label_e"];
            if (this.energy.property_type == 1) {
              energyPoints = parseFloat((this.independantSettings.price_points_settings.energy[labelE].single_family+'').replace(',', '.'));
            } else {
              energyPoints = parseFloat((this.independantSettings.price_points_settings.energy[labelE].multi_family+'').replace(',', '.'));
            }
          } else if (this.energy.building_construction_year <= 1991) {
            var labelD = this.energyLabelsIdBySlug["label_d"];
            if (this.energy.property_type == 1) {
              energyPoints = parseFloat((this.independantSettings.price_points_settings.energy[labelD].single_family+'').replace(',', '.'));
            } else {
              energyPoints = parseFloat((this.independantSettings.price_points_settings.energy[labelD].multi_family+'').replace(',', '.'));
            }
          } else if (this.energy.building_construction_year <= 1999) {
            var labelC = this.energyLabelsIdBySlug["label_c"];
            if (this.energy.property_type == 1) {
              energyPoints = parseFloat((this.independantSettings.price_points_settings.energy[labelC].single_family+'').replace(',', '.'));
            } else {
              energyPoints = parseFloat((this.independantSettings.price_points_settings.energy[labelC].multi_family+'').replace(',', '.'));
            }
          } else if (this.energy.building_construction_year <= 2001) {
            var labelB = this.energyLabelsIdBySlug["label_b"];
            if (this.energy.property_type == 1) {
              energyPoints = parseFloat((this.independantSettings.price_points_settings.energy[labelB].single_family+'').replace(',', '.'));
            } else {
              energyPoints = parseFloat((this.independantSettings.price_points_settings.energy[labelB].multi_family+'').replace(',', '.'));
            }
          } else if (this.energy.building_construction_year > 2001) {
            var labelA = this.energyLabelsIdBySlug["label_a"];
            if (this.energy.property_type == 1) {
              energyPoints = parseFloat((this.independantSettings.price_points_settings.energy[labelA].single_family+'').replace(',', '.'));
            } else {
              energyPoints = parseFloat((this.independantSettings.price_points_settings.energy[labelA].multi_family+'').replace(',', '.'));
            }
          }
        }
        // Legacy (pre-2026): monument_allowance checkbox floors negative energy at 0
        if (this.monumentAllowance.value && this.monumentAllowance.value == 1 && energyPoints < 0) {
          energyPoints = 0;
        }
        // 2026+: any monument type other than 1 (none) also floors negative energy at 0
        if (new Date(this.independantSettings.up_to_date) >= this.newVersion) {
          var sfMonumentType = parseInt(this.specialFeatures.monument_type.value || 1);
          if (sfMonumentType > 1 && energyPoints < 0) {
            energyPoints = 0;
          }
        }
        sum += energyPoints;
      }
      var total = this.roundToQuarterPoint(sum);
      Vue.set(this.energy, "energy_points", total);
      Vue.set(this.energy, "total", total);
      this.countCareHomePoints();
      this.countTotal();
    },
    countKitchenPoints() {
      var sum = 0;
      var maxPoints = 0;
      var addPoints = 0;
      if (this.kitchen.worktop_length.value == 2) {
        addPoints = parseFloat((this.independantSettings.price_points_settings.kitchen.worktop_1_2m_points + "").replace(",", ".")); //4
        maxPoints = parseFloat((this.independantSettings.price_points_settings.kitchen.worktop_1_2m_max_points + "").replace(",", ".")); //8
        
      } else if (this.kitchen.worktop_length.value == 3) {
        addPoints = parseFloat((this.independantSettings.price_points_settings.kitchen.worktop_2m_points + "").replace(",", ".")); //7
        maxPoints = parseFloat((this.independantSettings.price_points_settings.kitchen.worktop_2m_max_points + "").replace(",", ".")); //14
      }
      Vue.set(this.kitchen.worktop_length, "points", addPoints);
      Vue.set(this.kitchen.worktop_length, "total", addPoints);
      sum += addPoints;

      for (var ob in this.kitchen) {
        var element = this.kitchen[ob];
        var points = 0;
        if (ob == "cooker") {
          if (element.value == 1) {
            points = parseFloat((this.independantSettings.price_points_settings.kitchen.gas_cooker_points + "").replace(",", ".")); //0.5
          } else if (element.value == 2) {
            points = parseFloat((this.independantSettings.price_points_settings.kitchen.ceramic_cooker_points + "").replace(",", ".")); //1
          } else if (element.value == 3) {
            points = parseFloat((this.independantSettings.price_points_settings.kitchen.induction_cooker_points + "").replace(",", ".")); //1.25
          }
          Vue.set(this.kitchen[ob], "total", points);
        } else if (ob == "oven") {
          if (element.value == 1) {
            points = parseFloat((this.independantSettings.price_points_settings.kitchen.gas_oven_points + "").replace(",", ".")); //0.75
          } else if (element.value == 2) {
            points = parseFloat((this.independantSettings.price_points_settings.kitchen.electric_oven_points + "").replace(",", ".")); //1.25
          }
          Vue.set(this.kitchen[ob], "total", points);
        } else if (ob != "total" && ob != "worktop_length") {
          if(new Date(this.independantSettings.up_to_date) >= this.newVersion && element.new == 1){
            points = parseFloat((element.points + "").replace(",", ".")) * Math.round(parseFloat((element.value + "").replace(",", ".")));
            Vue.set(this.kitchen[ob], "total", points);
          }
        }
        if (sum + points <= maxPoints) {
          sum += points;
        } else {
          sum = maxPoints;
        }
      }
      var total = this.roundToQuarterPoint(sum);
      Vue.set(this.kitchen, "total", total);
      this.countCareHomePoints();
      this.countTotal();
    },
    // Sanitary (Rubriek 6): the "douche/bad" cap bounds ONLY the base shower/bath
    // points plus the douche/bad "extra" features, combined — not toilets/washbasins/
    // the separate-toilet correction. The cap is applied to the AGGREGATE extras total
    // (min(base + extras, cap)), not sequentially per field, so the result no longer
    // depends on object-key iteration order. Mirrors
    // AbstractIndependentRuleset::calcSanitary on the backend.
    countSanitaryPoints() {
      var sum = 0;
      var basePoints = 0;
      var doucheBadCap = 0;

      if (this.sanitary.shower_bath.value == 1) {
        basePoints = parseFloat((this.independantSettings.price_points_settings.sanitary.shower_points +'').replace(',', '.')); //4
        doucheBadCap = parseFloat((this.independantSettings.price_points_settings.sanitary.shower_max_points +'').replace(',', '.')); //8
      } else if (this.sanitary.shower_bath.value == 2) {
        basePoints = parseFloat((this.independantSettings.price_points_settings.sanitary.bath_points +'').replace(',', '.')); //6
        doucheBadCap = parseFloat((this.independantSettings.price_points_settings.sanitary.bath_max_points +'').replace(',', '.')); //12
      } else if (this.sanitary.shower_bath.value == 3) {
        basePoints = parseFloat((this.independantSettings.price_points_settings.sanitary.shower_bath_points +'').replace(',', '.')); //7
        doucheBadCap = parseFloat((this.independantSettings.price_points_settings.sanitary.shower_bath_max_points +'').replace(',', '.')); //14
      }
      Vue.set(this.sanitary.shower_bath, "points", basePoints);
      Vue.set(this.sanitary.shower_bath, "total", basePoints);

      if (this.sanitary.number_of_toilets.value) {
        var p = parseFloat((this.sanitary.number_of_toilets.points+'').replace(',', '.')) * Math.round(parseFloat((this.sanitary.number_of_toilets.value + "").replace(",", ".")));
        sum += p;
        Vue.set(this.sanitary.number_of_toilets, "total", p);
      }
      if (this.sanitary.number_of_washbasins.value) {
        var p = parseFloat((this.sanitary.number_of_washbasins.points+'').replace(',', '.')) * Math.round(parseFloat((this.sanitary.number_of_washbasins.value + "").replace(",", ".")));
        sum += p;
        Vue.set(this.sanitary.number_of_washbasins, "total", p);
      }

      var extrasSum = 0;
      // Official rule: power outlets are valued per washbasin (max 2 per washbasin),
      // so the cap must scale with the number of washbasins, not be a flat constant.
      var washbasinCount = Math.round(parseFloat((this.sanitary.number_of_washbasins.value + "").replace(",", ".")) || 0);
      var powerOutletMax = washbasinCount * parseFloat((this.independantSettings.price_points_settings.sanitary.power_outlet_max_points + "").replace(",", "."));

      for (var ob in this.sanitary) {
        var element = this.sanitary[ob];
        var points = 0;
        if (ob == "power_outlet") {
          if(new Date(this.independantSettings.up_to_date) >= this.newVersion && element.new == 1){
            points = this.sanitary.number_of_power_outlet.value * parseFloat((element.points+'').replace(',', '.'));
            if (points > powerOutletMax) {
              points = powerOutletMax;
            }
          }
          Vue.set(this.sanitary[ob], "total", points);
          extrasSum += points;
        } else if (ob == "number_of_separate_toilets") {
          if(new Date(this.independantSettings.up_to_date) >= this.newVersion && element.new == 1){
            if((parseFloat((this.sanitary.number_of_toilets.value+'').replace(',', '.')) || 0) < (parseFloat((this.sanitary.number_of_separate_toilets.value+'').replace(',', '.')) || 0)){
              var nt =  this.sanitary.number_of_toilets.value;
              Vue.set(this.sanitary.number_of_separate_toilets, "value", nt);
            }
            points = -((parseFloat((this.sanitary.number_of_toilets.value+'').replace(',', '.')) || 0) - (parseFloat((this.sanitary.number_of_separate_toilets.value+'').replace(',', '.')) || 0)) * parseFloat((element.points+'').replace(',', '.'));
          }
          Vue.set(this.sanitary[ob], "total", points);
          sum += points;
        } else if (ob == "number_of_hunged_toilets") {
          // Hanging-toilet correction is toilet valuation, not a douche/bad "extra
          // provision" — must stay uncapped like the other toilet corrections.
          if(new Date(this.independantSettings.up_to_date) >= this.newVersion && element.new == 1){
            points = parseFloat((element.points+'').replace(',', '.')) * Math.round(parseFloat((element.value + "").replace(",", ".")) || 0);
          }
          Vue.set(this.sanitary[ob], "total", points);
          sum += points;
        } else if (
          ob != "total" &&
          ob != "number_of_toilets" &&
          ob != "number_of_separate_toilets" &&
          ob != "number_of_hunged_toilets" &&
          ob != "number_of_washbasins" &&
          ob != "number_of_power_outlet" &&
          ob != "shower_bath"
        ) {
          if(new Date(this.independantSettings.up_to_date) >= this.newVersion && element.new == 1){
            points = parseFloat((element.points+'').replace(',', '.')) * Math.round(parseFloat((element.value + "").replace(",", ".")) || 0);
          }
          Vue.set(this.sanitary[ob], "total", points);
          extrasSum += points;
        }
      }

      sum += Math.min(basePoints + extrasSum, doucheBadCap);

      var total = this.roundToQuarterPoint(sum);
      Vue.set(this.sanitary, "total", total);
      this.countCareHomePoints();
      this.countTotal();
    },
    countDisabledPoints() {
      var sum = 0;
      var maxPoints = 0;      
      if (this.disabled.total_investment.value && parseFloat((this.disabled.total_investment.value + "").replace(",", ".")) > 0) {
        var p = parseFloat((this.disabled.total_investment.points + "").replace(",", ".")) * Math.floor(parseFloat((this.disabled.total_investment.value + "").replace(",", ".") / this.independantLandlordSpendingsBase));
        sum += p;
        Vue.set(this.disabled.total_investment, "total", p);
      }

      var total = this.roundToQuarterPoint(sum);
      Vue.set(this.disabled, "total", total);

      this.countCareHomePoints();
      this.countTotal();
    },
    countOutdoorPoints() {
      var sum = 0;
      var os = this.independantSettings.price_points_settings.outdoor || {};
      var ptsPerM2     = parseFloat((os.space_points || '0,35').replace(',', '.'));
      var commonPtsM2  = parseFloat((os.common_space_points || '0,75').replace(',', '.'));
      var minSpacePts  = parseFloat((os.min_space_points || 2).replace ? (os.min_space_points+'').replace(',','.') : os.min_space_points);
      var negPts       = parseFloat(((os.negative_common_space_points || 5) + '').replace(',', '.'));

      if (this.outdoor.spaces && this.outdoor.spaces.length) {
        // Facility-based multi-entry outdoor. Official rule (2.8 Buitenruimten): the
        // presence bonus (minSpacePts) is awarded ONCE for having any private outdoor
        // space at all, not once per garden/balcony/terrace entry — so private areas are
        // summed first and the bonus applied once, after the loop, on the total.
        var hasAny = false;
        var privateAreaSum = 0;
        this.outdoor.spaces.forEach(function(space) {
          var area = parseFloat((space.area + '').replace(',', '.')) || 0;
          if (area > 0) hasAny = true;
          if (parseInt(space.is_private) == 1 && area > 0) {
            privateAreaSum += area;
          } else if (parseInt(space.is_common) == 1 && area > 0) {
            var addrs = Math.max(1, parseInt(space.shared_addresses) || 1);
            sum += (area * commonPtsM2) / addrs;
          }
        });
        if (privateAreaSum > 0) {
          sum += minSpacePts + privateAreaSum * ptsPerM2;
        }
        if (!hasAny) {
          sum -= Math.abs(negPts);
        }
      } else {
        // Legacy flat fields
        if (this.outdoor.space && this.outdoor.space.value && parseFloat((this.outdoorSpacesArea + "").replace(",", ".")) > 0) {
          var points = minSpacePts + parseFloat((this.outdoorSpacesArea + "").replace(",", ".")) * ptsPerM2;
          Vue.set(this.outdoor.space, "total", points);
          sum += points;
        }
        if (this.outdoor.common_space && this.outdoor.common_space.value && parseFloat((this.outdoorCommonSpacesArea + "").replace(",", ".")) > 0 && parseFloat((this.outdoorCommonSpacesAddresses + "").replace(",", ".")) > 0) {
          var p = (parseFloat((this.outdoorCommonSpacesArea + "").replace(",", ".")) * commonPtsM2) / parseFloat((this.outdoorCommonSpacesAddresses + "").replace(",", "."));
          Vue.set(this.outdoor.common_space, "total", p);
          sum += p;
        }
        if (this.outdoorSpacesArea == 0 && this.outdoorCommonSpacesArea == 0) {
          sum -= Math.abs(negPts);
        }
      }

      // Official cap: rubriek 8 max 15 pts
      var maxOutdoor = parseFloat(((os.max_points || 15) + '').replace(',', '.'));
      var total = this.roundToQuarterPoint(Math.min(sum, maxOutdoor));
      Vue.set(this.outdoor, "total", total);

      this.countCareHomePoints();
      this.countTotal();
    },
    countVopPoints() {
      var sum = 0;
      var price = this.vop.price.value;
      var minVopPrice = parseFloat(this.independantMinVopPrice);
      var priceWasMinimum = false;
      // Official rule (unchanged across 2024-2026 policy books): the minimum WOZ
      // value is a fallback for a missing WOZ value only, never a floor applied
      // to a real (but low) known value.
      if ((!price || parseFloat(price) <= 0) && minVopPrice > 0) {
        price = minVopPrice;
        priceWasMinimum = true;
      }

      var pointsPerSurface = 0;
      var absolute = 0;
      var surfaceDivisor = parseFloat(this.independantVopProportionPrice + '');
      var usedAU = false;
      if (price) {
        absolute = (parseFloat(price) / parseFloat(this.independantVopPointPrice)).toFixed(2);
        var useAU = this.vop.location.value &&
          this.muncipalities.includes(this.vop.location.value) &&
          this.vopSurface < 40 &&
          this.vopSurface > 0;
        // 2026+: COROP alternate divisor only for delivery in 2018–2022
        if (useAU && this.independantSettings.price_points_settings.vop_minimum_fallback_only) {
          var vopDelivery = parseInt((this.vop.delivery.value || '0') + '');
          useAU = vopDelivery >= 2018 && vopDelivery <= 2022;
        }
        if (useAU) {
          surfaceDivisor = parseFloat(this.independantVopProportionPriceAmsterdamUtrecht);
          usedAU = true;
          pointsPerSurface = ((parseFloat(price) / parseFloat((this.vopSurface+'').replace(',', '.'))) / surfaceDivisor).toFixed(2);
        } else if (this.vopSurface > 0) {
          pointsPerSurface = ((parseFloat(price) / parseFloat((this.vopSurface+'').replace(',', '.'))) / surfaceDivisor).toFixed(2);
        }
      }


      // Preserve full precision (rawTotal, unrounded) alongside the official
      // quarter-point-rounded figure (roundedTotal) so the explanation can
      // show both, matching calcVop()'s raw_total/rounded_total in PHP.
      var rawTotal = parseFloat(absolute) + parseFloat(pointsPerSurface);
      var total = this.roundToQuarterPoint(rawTotal);
      Vue.set(this.vop, "total", total);
      this.countCareHomePoints();
      Vue.set(this.vop, "absolute", absolute);
      Vue.set(this.vop, "surface_proportion", pointsPerSurface);
      Vue.set(this.vop, "total", total);
      Vue.set(this.vop, "rawTotal", rawTotal.toFixed(4));
      Vue.set(this.vop, "roundedTotal", total);
      Vue.set(this.vop, "minimumApplied", false);
      Vue.set(this.vop, "minimumReason", null);
      Vue.set(this.vop, "capped", false);
      Vue.set(this.vop, "priceUsed", price ? parseFloat(price) : 0);
      Vue.set(this.vop, "priceWasMinimum", priceWasMinimum);
      Vue.set(this.vop, "divisorAbsolute", parseFloat(this.independantVopPointPrice));
      Vue.set(this.vop, "divisorSurface", surfaceDivisor);
      Vue.set(this.vop, "usedAU", usedAU);

      this.countTotal();
      if(new Date(this.independantSettings.up_to_date) >= this.newVersion){
        var pps = this.independantSettings.price_points_settings;
        var isVopMinFallbackOnlySetting = !!pps.vop_minimum_fallback_only;
        // Official rule: the 40-point WOZ minimum applies only when ALL of:
        //   1. newly built, or renovated to comply with new-build energy
        //      requirements at the time;
        //   2. that happened in [vop_nieuwbouw_year_from, vop_nieuwbouw_year_to]
        //      inclusive (default 2015-2019);
        //   3. every rubric except WOZ totals >= vop_nieuwbouw_other_min
        //      (default 110).
        // Applies uniformly across settings versions - not surface-dependent
        // (that's the separate, unrelated Amsterdam/Utrecht divisor rule
        // which happens to reuse the same "40" number for a different reason)
        // and not gated by vop_minimum_fallback_only.
        var isChecked = this.vop.built_between_2015_and_2019.value === 1
          || this.vop.built_between_2015_and_2019.value === '1'
          || this.vop.built_between_2015_and_2019.value === true;
        var yearRaw = this.vop.nieuwbouw_year && this.vop.nieuwbouw_year.value;
        var yearFrom = parseInt((pps.vop_nieuwbouw_year_from || 2015) + '');
        var yearTo = parseInt((pps.vop_nieuwbouw_year_to || 2019) + '');
        var year = yearRaw !== undefined && yearRaw !== null && yearRaw !== '' ? parseInt(yearRaw + '') : null;
        // A checked box with no year entered (or legacy data saved before
        // this field existed) cannot be verified against the window - default
        // to not qualifying rather than trusting an unverifiable checkbox.
        var isBuilt2015to2019 = isChecked && year !== null && year >= yearFrom && year <= yearTo;
        var otherMin = parseFloat(((pps.vop_nieuwbouw_other_min || 110) + '').replace(',', '.'));
        var floorPoints = parseFloat(((pps.vop_nieuwbouw_floor_points || 40) + '').replace(',', '.'));
        if(isBuilt2015to2019 && this.total - total >= otherMin && total < floorPoints){
          Vue.set(this.vop, "total", floorPoints);
          Vue.set(this.vop, "minimumApplied", true);
          Vue.set(this.vop, "minimumReason", 'nieuwbouw_renovation_2015_2019');
          this.countTotal();
        }
        if(this.total > 186 && !(this.vop.location.value && this.muncipalities.includes(this.vop.location.value) && this.vopSurface < 40 && this.vopSurface > 0) && total > (0.33 * this.total)){
          var otherTotal = this.total - total;
          Vue.set(this.vop, "capped", true);
          if(isVopMinFallbackOnlySetting && otherTotal < 187 && this.total >= 187){
            // 2026+ social-protection: WOZ cannot push a social property (non-WOZ < 187) to free market.
            total = Math.max(0, 186 - otherTotal);
            Vue.set(this.vop, "total", total);
            this.countTotal();
          } else if(this.total >= 187){
            total = 0.33 * this.total;
            Vue.set(this.vop, "total", total);
            this.countTotal();
          }
          if(this.total < 187){
            this.total = 186;
          }
        }
      }
    },
    countRenovationPoints() {
      var sum = 0;
      var price = this.renovation.price.value;

      var total = 0;
      if (price >= 10000) {
        total = this.roundToQuarterPoint((price / 1000) * parseFloat((this.independantSettings.price_points_settings.renovation.price_points+'').replace(',', '.')));//0.2
      }
      Vue.set(this.renovation, "total", total);

      this.countCareHomePoints();
      this.countTotal();
    },
    countSharedParkingPoints() {
      var points = 0;
      var addresses = parseFloat((this.sharedParking.number_of_addresses+'').replace(",", "."));
      if(addresses > 0 && this.sharedParking.type != 0 && this.sharedParking.type !== null){
        var typePoints = parseFloat((this.independantSettings.price_points_settings.shared_parking[this.sharedParking.type]+'' || 0).replace(',', '.'));
        var evPoints = (this.sharedParking.has_ev_charging && this.sharedParking.has_ev_charging == 1)
          ? parseFloat(((this.independantSettings.price_points_settings.shared_parking.ev_charging_points || 2) + '').replace(',', '.'))
          : 0;
        points = this.roundToQuarterPoint((typePoints + evPoints) / addresses);
      }
      Vue.set(this.sharedParking, "total", points);
      this.countTotal();
    },
    countCareHomePoints() {
      var sum = 0;
      var total = 0;
      if (this.careHome.value && this.careHome.value == 1) {
        total =
          this.leaveArea.total +
          this.otherSpaces.total +
          this.sharedParking.total +
          this.heating.total +
          this.energy.total +
          this.kitchen.total +
          this.sanitary.total +
          this.disabled.total +
          this.outdoor.total +
          this.vop.total +
          this.renovation.total;
        total = total * (parseFloat((this.careHome.points+'').replace(",", "."))/100);
      }
      if (this.careHome.has_automatic_bell && this.careHome.has_automatic_bell == 1) {
        total += parseFloat((this.careHome.automatic_bell_points).replace(",", "."));
      }
      Vue.set(this.careHome, "total", this.roundToQuarterPoint(total));
      this.countTotal();
    },
    countMonumentAllowancePoints() {
      var total = 0;
      // 2026+: monument handled by countSpecialFeaturesPoints (monument_type radio);
      // legacy monument_allowance rubric does not apply.
      if (new Date(this.independantSettings.up_to_date) < this.newVersion) {
        if (this.monumentAllowance.value && this.monumentAllowance.value == 1) {
          total = 50;
        }
      }
      Vue.set(this.monumentAllowance, "total", this.roundToQuarterPoint(total));
      this.countTotal();
    },
    countSpecialFeaturesPoints() {
      var isNew = new Date(this.independantSettings.up_to_date) >= this.newVersion;
      if (!isNew) {
        return;
      }
      var sf = this.independantSettings.price_points_settings.special_features || {};
      var points = 0;

      // Video/audio doorbell
      if (this.specialFeatures.video_phone.value == 1) {
        points += parseFloat((sf.video_phone_points || '0,25').toString().replace(',', '.'));
      }
      // Exclusive EV charging station
      if (this.specialFeatures.charging_station.value == 1) {
        points += parseFloat((sf.charging_station_points || '2,00').toString().replace(',', '.'));
      }
      // National monument pre-2024 (type 2) = legacy +50 pts
      if (parseInt(this.specialFeatures.monument_type.value || 1) === 2) {
        points += 50;
      }

      Vue.set(this.specialFeatures, 'total', this.roundToQuarterPoint(points));
      // Monument type also affects energy (floor at 0 for negative labels)
      this.countEnergyPoints();
      this.countTotal();
    },
    calcSurchargesForPrice(basePrice) {
      var isNew = new Date(this.independantSettings.up_to_date) >= this.newVersion;
      if (!isNew || !basePrice) {
        return { amount: 0, pct: 0, breakdown: {} };
      }
      var sf = this.independantSettings.price_points_settings.special_features || {};
      var surchargePct = 0;
      var breakdown = {};

      var monumentType = parseInt(this.specialFeatures.monument_type.value || 1);
      if (monumentType === 7) {
        var pct = parseFloat((sf.monument_national_percentage || '35').toString().replace(',', '.'));
        surchargePct += pct; breakdown.monument = pct;
      } else if (monumentType === 3) {
        var pct = parseFloat((sf.monument_municipal_provincial_percentage || '15').toString().replace(',', '.'));
        surchargePct += pct; breakdown.monument = pct;
      } else if (monumentType === 9) {
        var pct = parseFloat((sf.protected_view_percentage || '5').toString().replace(',', '.'));
        surchargePct += pct; breakdown.monument = pct;
      }

      if (this.specialFeatures.new_build.value == 1) {
        // Middle sector only: 144–186 pts (Wet betaalbare huur, Art. 5a)
        var minPts = parseInt((sf.new_build_min_points || '144').toString());
        var maxPts = parseInt((sf.new_build_max_points || '186').toString());
        if (this.totalPoints >= minPts && this.totalPoints <= maxPts) {
          var firstUseStr = (this.specialFeatures.new_build_first_use_date.value || '').toString().trim();
          var firstUseDate = null;
          if (firstUseStr.length === 10) {
            var fParts = firstUseStr.split('-');
            if (fParts.length === 3) {
              // theme-calendar emits DD-MM-YYYY; fallback accepts YYYY-MM-DD
              firstUseDate = fParts[0].length === 4
                ? new Date(firstUseStr)
                : new Date(parseInt(fParts[2]), parseInt(fParts[1]) - 1, parseInt(fParts[0]));
            }
          }
          var before2028 = this.specialFeatures.construction_started_before_2028.value != 0;
          if (firstUseDate && !isNaN(firstUseDate.getTime()) && firstUseDate >= new Date('2024-07-01') && before2028) {
            var pct = parseFloat((sf.new_build_percentage || '10').toString().replace(',', '.'));
            surchargePct += pct; breakdown.new_build = pct;
          }
        }
      }

      var amount = Math.round(basePrice * (surchargePct / 100) * 100) / 100;
      return { amount: amount, pct: surchargePct, breakdown: breakdown };
    },
    countTotal() {
      var isNew = new Date(this.independantSettings.up_to_date) >= this.newVersion;
      var total =
        this.leaveArea.total +
        this.otherSpaces.total +
        this.heating.total +
        this.energy.total +
        this.kitchen.total +
        this.sanitary.total +
        this.disabled.total +
        this.outdoor.total +
        this.sharedParking.total +
        this.vop.total +
        parseFloat((this.renovation.total+'').replace(",", ".")) +
        this.careHome.total +
        this.monumentAllowance.total;
      if (isNew) {
        total += (this.specialFeatures.total || 0);
      }
      this.total = Math.round(total);
      this.totalPoints =
        this.total && parseFloat(this.total + "") > 40 ? this.total : 40;
      var basePrice = this.lookupRentByPoints(this.totalPoints);
      if (isNew && basePrice > 0) {
        var surcharges = this.calcSurchargesForPrice(basePrice);
        Vue.set(this.specialFeatures, 'surchargeAmount', surcharges.amount);
        Vue.set(this.specialFeatures, 'surchargePct', surcharges.pct);
        Vue.set(this.specialFeatures, 'surchargeBreakdown', surcharges.breakdown);
        Vue.set(this.specialFeatures, 'basePrice', basePrice);
        this.price = Math.round((basePrice + surcharges.amount) * 100) / 100;
      } else {
        Vue.set(this.specialFeatures, 'surchargeAmount', 0);
        Vue.set(this.specialFeatures, 'surchargePct', 0);
        Vue.set(this.specialFeatures, 'surchargeBreakdown', {});
        Vue.set(this.specialFeatures, 'basePrice', basePrice);
        this.price = basePrice || 0;
      }
    },
    /**
     * "Download PDF" no longer submits the form natively: it saves the CURRENT form state
     * (including anything not yet committed via the separate "Opslaan" button) through the
     * exact same server-side save/normalize/WwsCalculator flow first, waits for that save to
     * succeed, replaces this page's client-computed total/price with the authoritative saved
     * result, and only then opens the PDF for that EXACT saved calculation id/revision — so
     * the website and the PDF always show the identical, server-authoritative numbers for
     * the identical saved data, never a stale calculation, a different settings version, or
     * this component's own locally-summed countTotal() preview.
     */
    downloadPdf() {
      if (this.downloading) {
        return; // a request is already in flight — ignore the duplicate/double click
      }
      this.downloading = true;
      this.downloadRequestSeq += 1;
      var requestId = this.downloadRequestSeq;
      var that = this;

      var formEl = document.getElementById('form');
      var formData = new FormData(formEl);

      $.ajax({
        url: formEl.action,
        method: 'post',
        data: formData,
        processData: false,
        contentType: false,
        // dataType: 'json' is what makes Laravel's Request::expectsJson() route this to the
        // JSON save-response branch (Modules\Property\Http\Controllers\Api\PropertyController
        // ::calculateRentPrice()) instead of the plain-form-submit redirect/PDF-bytes paths.
        dataType: 'json',
        success: function (response) {
          // A newer click (or this click's own eventual response arriving after a newer one
          // already started) must never act on a stale result.
          if (requestId !== that.downloadRequestSeq) {
            return;
          }
          that.downloading = false;
          that.total = response.total_points;
          that.totalPoints = response.total_points;
          that.price = response.max_rent_price;
          // Keep the surcharge breakdown box (base rent / surcharge %+amount, shown just
          // above the Download PDF/Opslaan buttons) consistent with the new authoritative
          // total/price instead of leaving it showing whatever countTotal() computed for the
          // PREVIOUS total — those are derived from this.totalPoints too, so a stale
          // breakdown next to a fresh total is a smaller version of the exact mismatch this
          // whole save-before-download flow exists to fix.
          Vue.set(that.specialFeatures, 'basePrice', response.base_rent);
          Vue.set(that.specialFeatures, 'surchargePct', response.surcharge_pct);
          Vue.set(that.specialFeatures, 'surchargeAmount', response.surcharge_amount);
          Vue.set(that.specialFeatures, 'surchargeBreakdown', response.surcharge_breakdown || {});

          var lang = formData.get('lang') || 'nl';
          var pdfUrl = '/api/calculate-prices/' + that.property.id + '/' + response.calculation_id + '/pdf'
            + '?revision=' + encodeURIComponent(response.revision)
            + '&lang=' + encodeURIComponent(lang);
          window.open(pdfUrl, '_blank');
        },
        error: function (jqXHR) {
          if (requestId !== that.downloadRequestSeq) {
            return;
          }
          that.downloading = false;
          var body = jqXHR && jqXHR.responseJSON;
          var message = (body && body.message)
            || (that.locales && that.locales.common && that.locales.common.something_went_wrong)
            || 'Something went wrong. Please try again.';
          if (body && body.errors) {
            var firstErrors = Object.values(body.errors).map(function (list) { return list[0]; });
            if (firstErrors.length) {
              message = firstErrors.join('\n');
            }
          }
          sweetalert((that.locales && that.locales.common && that.locales.common.error) || 'Oops!', message, 'error');
        }
      });
    },
    lookupRentByPoints(points) {
      var prices = this.independantMaxRentalPrices;
      var p = Math.max(40, Math.round(parseFloat(points) || 40));
      if (p <= 250) {
        return parseFloat((prices[p] + '').replace(',', '.')) || 0;
      }
      var price250 = parseFloat((prices[250] + '').replace(',', '.')) || 0;
      var price249 = parseFloat((prices[249] + '').replace(',', '.')) || 0;
      if (price250 > 0 && price249 > 0) {
        var marginalRate = price250 - price249;
        return Math.round((price250 + (p - 250) * marginalRate) * 100) / 100;
      }
      return price250;
    },
    formatPts(val) {
      if (val === null || val === undefined || val === '') return '';
      var n = parseFloat(String(val).replace(',', '.'));
      if (isNaN(n)) return '';
      if (n % 1 === 0) return String(Math.round(n));
      return String(n).replace('.', ',');
    },
    /**
     * Renders a fixed point value as "(+2 points)" / "(-4 points)" /
     * "(0 points)" / "(+0,65 points per m²)", reusing formatPts() for the
     * locale-correct numeral. Centralizes sign/pluralization/wrapping so
     * option labels never hand-format point values inline.
     * opts.suffix: pre-translated unit phrase appended after the word
     *   ("per m²", "per verwarmd vertrek", ...).
     * opts.wrap: pass false to omit the surrounding parentheses (used when
     *   composing a longer rule sentence around the base pointsLabel()).
     */
    pointsLabel(val, opts) {
      opts = opts || {};
      if (val === null || val === undefined || val === '') return '';
      var n = parseFloat(String(val).replace(',', '.'));
      if (isNaN(n)) return '';
      var formatted = this.formatPts(n);
      var signed = n > 0 ? ('+' + formatted) : formatted;
      var word = Math.abs(n) === 1
        ? ((this.locales && this.locales.properties && this.locales.properties.point) || 'point')
        : ((this.locales && this.locales.properties && this.locales.properties.points) || 'points');
      var text = signed + ' ' + word;
      if (opts.suffix) {
        text += ' ' + opts.suffix;
      }
      return opts.wrap === false ? text : '(' + text + ')';
    },
    /** Renders a percentage rule as "(+15%)" - for surcharges on rent price, not points. */
    percentLabel(val, opts) {
      opts = opts || {};
      if (val === null || val === undefined || val === '') return '';
      var n = parseFloat(String(val).replace(',', '.'));
      if (isNaN(n)) return '';
      var formatted = this.formatPts(n);
      var signed = n > 0 ? ('+' + formatted) : formatted;
      var text = signed + '%';
      if (opts.suffix) {
        text += ' ' + opts.suffix;
      }
      return opts.wrap === false ? text : '(' + text + ')';
    },
  },
  };
</script>
